import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import ActualsUploader from "./ActualsUploader";
import BudgetUploader from "./BudgetUploader";
import AdminUsers from "./AdminUsers";
import AdminBusinessUnits from "./AdminBusinessUnits";
import ActualsDashboard from "./ActualsDashboard";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import "./AdminPanel.css";
import "./Dashboard.css";

// ─── Category colors (shared with Dashboard) ─────────────────────────────────
const CAT_COLOR = (cat: string): string => ({
  COMPENSATION:     "#4A90E2",
  OCCUPANCY:        "#e84393",
  OPERATING:        "#ea580c",
  ADVERTISING:      "#7c3aed",
  ALLOCATED:        "#475569",
  AMORT:            "#64748b",
} as Record<string, string>)[cat.toUpperCase().replace(/\s/g, "")] ?? "#94a3b8";

const EXCLUDED_CATS = new Set(["Corp OH", "CORP_OH", "Uncategorized", "OtherAdjustments", "OTHERADJUSTMENTS"]);

// ─── Types ───────────────────────────────────────────────────────────────────

interface Account {
  account:  string;
  category: string;
  cc_count: number;
  fiscal_years?: string | null;
}

interface UnmappedAccount {
  id:          number;
  account:     string;
  category:    string;
  budget_rows: number;
  actual_rows: number;
}

interface AcctDraft {
  account:  string;
  category: string;
  editing:  boolean;
  saving:   boolean;
  feedback: { ok: boolean; msg: string } | null;
}

interface DimWindow {
  dimension: string;
  fiscal_year?: string;
  open_from: string; // "YYYY-MM-DD"
  open_to:   string;
}

interface CostCenter {
  cost_center_number: string;
  cost_center_name:   string;
  cost_center_owner?: string;
  business_unit:      string;
  business_unit_id?:  number;
  email?:             string;
  role?:              string;
  is_first_login:     number;
  deactivated_from_fy?: string | null;
}

interface BUOption { id: number; code: string; name: string; }
interface UserOption { id: number; name: string; email: string; owned_cost_center?: string; }

// Draft state per CC row (keyed by original CC # in ccDrafts map)
interface CCDraft {
  cost_center_number: string; // editable — may differ from key during edit
  cost_center_name:   string;
  business_unit:      string;
  business_unit_id:   number | null;
  editing:   boolean;
  saving:    boolean;
  resetting: boolean;
  feedback: { ok: boolean; msg: string } | null;
}

interface CategoryKPI {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  top_over:  CCRow[];
  top_under: CCRow[];
}

interface CCRow {
  cost_center:      string;
  cost_center_name: string;
  business_unit:    string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface CCCatBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface AcctRow {
  account:        string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface VRAccountRow {
  account:        string;
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface VRCategoryBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface VRCostCenter {
  cost_center:          string;
  cost_center_name:     string;
  business_unit:        string;
  month_budget:         number;
  month_actual:         number;
  month_variance:       number;
  ytd_budget:           number;
  ytd_actual:           number;
  ytd_variance:         number;
  top_unfavorable:      VRAccountRow[];
  top_favorable:        VRAccountRow[];
  category_breakdown?:  VRCategoryBreakdown[];
}

interface VRActionPlanItem {
  id: number;
  cost_center: string;
  action_text: string;
  category: string | null;
  status: "OPEN" | "CLOSED";
  created_by: string | null;
}

interface BUSummaryRow {
  business_unit:  string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface ForecastCatKPI {
  category:      string;
  dim_a:         number;
  dim_b:         number;
  change:        number;
  top_increased: ForecastCCRow[];
  top_decreased: ForecastCCRow[];
  all_ccs:       ForecastCCRow[];
}

interface ForecastCCRow {
  cost_center:      string;
  cost_center_name: string;
  business_unit:    string;
  dim_a:  number;
  dim_b:  number;
  change: number;
}

interface ForecastBUSummaryRow {
  business_unit: string;
  dim_a:  number;
  dim_b:  number;
  change: number;
}

interface ActualsCatKPI {
  category:     string;
  actual:       number;
  top_spenders: ActualsCCRow[];
}

interface ActualsCCRow {
  cost_center:      string;
  cost_center_name: string;
  actual: number;
}

interface MonthlyTrendPoint {
  month:      string;
  short:      string;
  budget:     number;
  actual:     number;
  hasActuals: boolean;
}

interface ActualsAcctRow {
  account: string;
  actual:  number;
}

// Draft state per dimension row
interface DimDraft {
  open_from:      string;
  open_to:        string;
  orig_open_from: string; // server value — restored on Cancel
  orig_open_to:   string;
  editing:  boolean;
  saving:   boolean;
  feedback: { ok: boolean; msg: string } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ALL_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"] as const;

/** Returns the most relevant forecast cycle for today's date. */
function getDefaultDim(): string {
  const m = new Date().getMonth() + 1; // 1–12
  if (m >= 4 && m <= 6) return "July Forecast";
  if (m >= 7 && m <= 9) return "October Forecast";
  return "January Forecast"; // Jan–Mar and Oct–Dec
}

function fmtDate(d: string): string {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function fmtMoney(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${Math.round(abs / 1_000)}k`;
  return `$${Math.round(abs)}`;
}

/** Accounting notation: unfavorable (positive variance) in brackets, favorable plain */
function fmtVar(n: number): string {
  const val = fmtMoney(n);
  return n > 0 ? `(${val})` : val;
}

// ─── Fiscal-year helpers ─────────────────────────────────────────────────────

// FY{N} = April{N} → March{N+1}  (e.g. FY26 = Apr 2026 – Mar 2027)
// FY is named after the calendar year it starts in:
//   Apr 2026 – Mar 2027 = FY26.  So May 2026 → FY26.
function autoFY(): string {
  const now = new Date();
  const yr  = now.getFullYear();
  return now.getMonth() < 3          // Jan–Mar → still in previous year's FY
    ? `FY${String(yr - 1).slice(-2)}`
    : `FY${String(yr).slice(-2)}`;    // Apr–Dec → current year's FY
}

function buildFYOptions(): string[] {
  const yr = new Date().getFullYear();
  const opts: string[] = [];
  for (let y = yr - 1; y <= yr + 3; y++) opts.push(`FY${String(y).slice(-2)}`);
  return opts;
}
const FY_OPTIONS = buildFYOptions();

// ─── Component ───────────────────────────────────────────────────────────────

type Tab = "forecast" | "costcenters" | "accounts" | "dashboard" | "buforecast" | "upload" | "salary" | "finance" | "businessunits" | "users" | "variancereport";

const TAB_FROM_URL: Record<string, Tab> = {
  "forecast-windows": "forecast",
  "cost-centers":     "costcenters",
  "accounts":         "accounts",
  "bu-dashboard":     "dashboard",
  "bu-forecast":      "buforecast",
  "data-upload":      "upload",
  "salary-config":    "salary",
  "finance-view":     "finance",
  "business-units":   "businessunits",
  "users":            "users",
  "variance-report":  "variancereport",
};

const URL_FROM_TAB: Record<Tab, string> = {
  forecast:        "forecast-windows",
  costcenters:     "cost-centers",
  accounts:        "accounts",
  dashboard:       "bu-dashboard",
  buforecast:      "bu-forecast",
  upload:          "data-upload",
  salary:          "salary-config",
  finance:         "finance-view",
  businessunits:   "business-units",
  users:           "users",
  variancereport:  "variance-report",
};

// ─── BU Leader Card sub-component (used in Cost Centers tab) ─────────────────

interface BULeaderCardProps {
  leader: CostCenter;
  allCostCenters: CostCenter[];
  onRefresh: () => void;
}

const BULeaderCard: React.FC<BULeaderCardProps> = ({ leader, allCostCenters, onRefresh }) => {
  const [assignments, setAssignments] = useState<{ assigned_cc: string; cost_center_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingCC, setAddingCC] = useState("");

  useEffect(() => {
    api.get(`/admin/bu-leaders/${leader.cost_center_number}/assignments`)
      .then(res => { setAssignments(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [leader.cost_center_number]);

  const assignCC = () => {
    if (!addingCC) return;
    api.post(`/admin/bu-leaders/${leader.cost_center_number}/assignments`, { assigned_ccs: [addingCC] })
      .then(() => {
        const ccInfo = allCostCenters.find(c => c.cost_center_number === addingCC);
        setAssignments(prev => [...prev, { assigned_cc: addingCC, cost_center_name: ccInfo?.cost_center_name || addingCC }]);
        setAddingCC("");
      })
      .catch(() => {});
  };

  const removeAssignment = (assignedCC: string) => {
    api.delete(`/admin/bu-leaders/${leader.cost_center_number}/assignments/${assignedCC}`)
      .then(() => setAssignments(prev => prev.filter(a => a.assigned_cc !== assignedCC)))
      .catch(() => {});
  };

  const demote = () => {
    api.put(`/admin/cost-centers/${leader.cost_center_number}/role`, { role: "user" })
      .then(() => onRefresh())
      .catch(() => {});
  };

  // CCs available to assign (not already assigned and not the leader itself)
  const assignedSet = new Set(assignments.map(a => a.assigned_cc));
  const availableCCs = allCostCenters.filter(
    cc => cc.cost_center_number !== leader.cost_center_number && !assignedSet.has(cc.cost_center_number)
  );

  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span className="material-icons" style={{ fontSize: "18px", color: "#6366f1" }}>person</span>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
            {leader.cost_center_number} – {leader.cost_center_name}
          </span>
          <span style={{ fontSize: "12px", color: "#6b7280" }}>({leader.cost_center_owner})</span>
        </div>
        <button
          className="admin-btn admin-btn--ghost"
          onClick={demote}
          title="Remove BU Leader role"
          style={{ fontSize: "11px" }}
        >
          <span className="material-icons" style={{ fontSize: "14px" }}>arrow_downward</span>
          Demote
        </button>
      </div>

      {/* Assigned CCs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
        {loading ? (
          <span style={{ fontSize: "12px", color: "#9ca3af" }}>Loading…</span>
        ) : assignments.length === 0 ? (
          <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>No cost centers assigned yet</span>
        ) : (
          assignments.map(a => (
            <span
              key={a.assigned_cc}
              style={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                background: "#e0e7ff", color: "#3730a3", fontSize: "12px",
                padding: "2px 8px", borderRadius: "4px", fontWeight: 500,
              }}
            >
              {a.assigned_cc} – {a.cost_center_name}
              <button
                onClick={() => removeAssignment(a.assigned_cc)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}
                title="Remove assignment"
              >
                <span className="material-icons" style={{ fontSize: "14px", color: "#6366f1" }}>close</span>
              </button>
            </span>
          ))
        )}
      </div>

      {/* Add CC */}
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <select
          className="admin-select"
          value={addingCC}
          onChange={e => setAddingCC(e.target.value)}
          style={{ fontSize: "12px" }}
        >
          <option value="">Assign a cost center…</option>
          {availableCCs.map(cc => (
            <option key={cc.cost_center_number} value={cc.cost_center_number}>
              {cc.cost_center_number} – {cc.cost_center_name}
            </option>
          ))}
        </select>
        <button
          className="admin-btn admin-btn--primary"
          onClick={assignCC}
          disabled={!addingCC}
          style={{ fontSize: "11px", padding: "4px 10px" }}
        >
          <span className="material-icons" style={{ fontSize: "14px" }}>add</span>
          Assign
        </button>
      </div>
    </div>
  );
};

// ─── Main AdminPanel Component ───────────────────────────────────────────────

// Tab grouping
const ADMIN_SETUP_TABS: Tab[] = ["businessunits", "costcenters", "users", "accounts"];
const FORECAST_SETUP_TABS: Tab[] = ["forecast", "salary", "upload", "dashboard", "variancereport", "buforecast", "finance"];

const AdminPanel: React.FC = () => {
  const { tab: tabParam } = useParams<{ tab: string }>();
  const navigate          = useNavigate();
  const activeTab: Tab    = (tabParam && TAB_FROM_URL[tabParam]) ? TAB_FROM_URL[tabParam] : "dashboard";
  const setActiveTab      = (tab: Tab) => navigate(`/admin/${URL_FROM_TAB[tab]}`);
  const isAdminSetupGroup = ADMIN_SETUP_TABS.includes(activeTab);

  // ── Dimension Windows state ──────────────────────────────────────────────
  const [dimDrafts, setDimDrafts] = useState<Record<string, DimDraft>>({});
  const [dimsLoading, setDimsLoading] = useState(true);
  const [forecastActive, setForecastActive] = useState(false);
  const [selectedDim, setSelectedDim] = useState<string | null>(null);
  const [selectionMsg, setSelectionMsg] = useState<string | null>(null);
  const selectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Active Fiscal Year state ─────────────────────────────────────────────
  const [currentFY,  setCurrentFY]  = useState<string>(autoFY());
  const [fyDraft,    setFyDraft]    = useState<string>(autoFY());
  const [fyLoading,  setFyLoading]  = useState(false);
  const [fyLoaded,   setFyLoaded]   = useState(false); // true once FY fetched from backend
  const [fyFeedback, setFyFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // ── Password-reset email setting ──────────────────────────────────────────
  const [resetEmail,         setResetEmail]         = useState("");
  const [resetEmailDraft,    setResetEmailDraft]    = useState("");
  const [resetEmailLoading,  setResetEmailLoading]  = useState(false);
  const [resetEmailFeedback, setResetEmailFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // ── Forecast variance allowed setting ─────────────────────────────────────
  const [varianceAllowed,        setVarianceAllowed]        = useState<number>(0);
  const [varianceAllowedDraft,   setVarianceAllowedDraft]   = useState<string>("0");
  const [varianceAllowedLoading, setVarianceAllowedLoading] = useState(false);
  const [varianceAllowedFeedback,setVarianceAllowedFeedback]= useState<{ ok: boolean; msg: string } | null>(null);

  // ── Help URL settings ───────────────────────────────────────────────────
  const [helpUrlAdmin,         setHelpUrlAdmin]         = useState("");
  const [helpUrlAdminDraft,    setHelpUrlAdminDraft]    = useState("");
  const [helpUrlUser,          setHelpUrlUser]          = useState("");
  const [helpUrlUserDraft,     setHelpUrlUserDraft]     = useState("");
  const [helpUrlLoading,       setHelpUrlLoading]       = useState(false);
  const [helpUrlFeedback,      setHelpUrlFeedback]      = useState<{ ok: boolean; msg: string } | null>(null);

  const loadDimWindows = useCallback(() => {
    setDimsLoading(true);
    api.get(`/admin/dimension-windows?fy=${encodeURIComponent(currentFY)}`)
      .then(res => {
        const drafts: Record<string, DimDraft> = {};
        (res.data as DimWindow[]).forEach(w => {
          const from = w.open_from.slice(0, 10);
          const to   = w.open_to.slice(0, 10);
          drafts[w.dimension] = {
            open_from: from, open_to: to,
            orig_open_from: from, orig_open_to: to,
            editing: false, saving: false, feedback: null,
          };
        });
        ALL_DIMS.forEach(dim => {
          if (!drafts[dim]) {
            drafts[dim] = {
              open_from: "", open_to: "",
              orig_open_from: "", orig_open_to: "",
              editing: false, saving: false, feedback: null,
            };
          }
        });
        setDimDrafts(drafts);
        setDimsLoading(false);
      })
      .catch(() => setDimsLoading(false));
  }, [currentFY]);

  const loadForecastConfig = useCallback(() => {
    api.get("/settings/forecast-config")
      .then(res => {
        setForecastActive(res.data.active);
        const dim = res.data.dimension || null;
        if (dim) {
          setSelectedDim(dim);
        } else {
          // Nothing set in DB — auto-select a sensible default and persist it
          const defaultDim = getDefaultDim();
          setSelectedDim(defaultDim);
          api.put("/admin/active-dimension", { dimension: defaultDim }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDimWindows();
    loadForecastConfig();
    api.get("/admin/fiscal-year")
      .then(r => {
        const fy = r.data.fiscalYear || autoFY();
        setCurrentFY(fy);
        setFyDraft(fy);
      })
      .catch(() => {})
      .finally(() => setFyLoaded(true));
    api.get("/admin/settings/password-reset-email")
      .then(r => {
        setResetEmail(r.data.email || "");
        setResetEmailDraft(r.data.email || "");
      })
      .catch(() => {});
    api.get("/admin/settings/forecast-variance-allowed")
      .then(r => {
        const v = r.data.variance_allowed ?? 0;
        setVarianceAllowed(v);
        setVarianceAllowedDraft(String(v));
      })
      .catch(() => {});
    api.get("/admin/settings/help-urls")
      .then(r => {
        setHelpUrlAdmin(r.data.help_url_admin || "");
        setHelpUrlAdminDraft(r.data.help_url_admin || "");
        setHelpUrlUser(r.data.help_url_user || "");
        setHelpUrlUserDraft(r.data.help_url_user || "");
      })
      .catch(() => {});
  }, [loadDimWindows, loadForecastConfig]);

  const handleSaveFY = useCallback(async () => {
    setFyLoading(true);
    setFyFeedback(null);
    try {
      await api.put("/admin/fiscal-year", { fiscalYear: fyDraft });
      setCurrentFY(fyDraft);
      setFyFeedback({ ok: true, msg: `Active fiscal year set to ${fyDraft}` });
      // Notify header to update FY badge
      window.dispatchEvent(new CustomEvent("fy-changed", { detail: fyDraft }));
    } catch (err: any) {
      const msg = err.response?.data?.error || "Failed to save fiscal year";
      setFyFeedback({ ok: false, msg });
    } finally {
      setFyLoading(false);
    }
  }, [fyDraft]);

  const handleSaveResetEmail = async () => {
    setResetEmailLoading(true);
    setResetEmailFeedback(null);
    try {
      await api.put("/admin/settings/password-reset-email", { email: resetEmailDraft });
      setResetEmail(resetEmailDraft);
      setResetEmailFeedback({ ok: true, msg: "Saved" });
    } catch (err: any) {
      setResetEmailFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to save" });
    } finally {
      setResetEmailLoading(false);
    }
  };

  const handleSaveVarianceAllowed = async () => {
    const val = parseFloat(varianceAllowedDraft);
    if (isNaN(val) || val < 0) {
      setVarianceAllowedFeedback({ ok: false, msg: "Enter a valid dollar amount (0 or more)" });
      return;
    }
    setVarianceAllowedLoading(true);
    setVarianceAllowedFeedback(null);
    try {
      await api.put("/admin/settings/forecast-variance-allowed", { variance_allowed: val });
      setVarianceAllowed(val);
      setVarianceAllowedFeedback({ ok: true, msg: "Saved" });
    } catch (err: any) {
      setVarianceAllowedFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to save" });
    } finally {
      setVarianceAllowedLoading(false);
    }
  };

  const handleSaveHelpUrls = async () => {
    setHelpUrlLoading(true);
    setHelpUrlFeedback(null);
    try {
      await api.put("/admin/settings/help-urls", {
        help_url_admin: helpUrlAdminDraft.trim(),
        help_url_user: helpUrlUserDraft.trim(),
      });
      setHelpUrlAdmin(helpUrlAdminDraft.trim());
      setHelpUrlUser(helpUrlUserDraft.trim());
      setHelpUrlFeedback({ ok: true, msg: "Saved" });
    } catch (err: any) {
      setHelpUrlFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to save" });
    } finally {
      setHelpUrlLoading(false);
    }
  };

  const handleToggleForecast = () => {
    const next = !forecastActive;
    setForecastActive(next); // optimistic — fire and forget, no revert
    api.put("/admin/forecast-active", { active: next }).catch(() => {});
  };

  const handleSelectDim = (dim: string) => {
    setSelectedDim(dim);
    api.put("/admin/active-dimension", { dimension: dim }).catch(() => {});

    // Build the toast message using already-loaded window dates
    const draft = dimDrafts[dim];
    let msg: string;
    if (dim === "Annual Budget") {
      msg = "Annual Budget selected — always open for input";
    } else if (draft?.open_from && draft?.open_to) {
      msg = `${dim} selected — open for input from ${fmtDate(draft.open_from)} to ${fmtDate(draft.open_to)}`;
    } else {
      msg = `${dim} selected`;
    }
    setSelectionMsg(msg);
    if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current);
    selectionTimerRef.current = setTimeout(() => setSelectionMsg(null), 5000);
  };

  const startEditing = (dim: string) => {
    setDimDrafts(prev => ({ ...prev, [dim]: { ...prev[dim], editing: true, feedback: null } }));
  };

  const cancelEditing = (dim: string) => {
    setDimDrafts(prev => ({
      ...prev,
      [dim]: {
        ...prev[dim],
        open_from: prev[dim].orig_open_from,
        open_to:   prev[dim].orig_open_to,
        editing: false,
        feedback: null,
      },
    }));
  };

  const updateDimField = (dim: string, field: "open_from" | "open_to", val: string) => {
    setDimDrafts(prev => ({
      ...prev,
      [dim]: { ...prev[dim], [field]: val, feedback: null },
    }));
  };

  const saveDimWindow = async (dim: string) => {
    const draft = dimDrafts[dim];
    setDimDrafts(prev => ({ ...prev, [dim]: { ...prev[dim], saving: true, feedback: null } }));
    try {
      await api.put(`/admin/dimension-windows/${encodeURIComponent(dim)}`, {
        open_from:    draft.open_from,
        open_to:      draft.open_to,
        fiscal_year:  currentFY,
      });
      setDimDrafts(prev => ({
        ...prev,
        [dim]: {
          ...prev[dim],
          saving: false,
          editing: false,
          orig_open_from: prev[dim].open_from,
          orig_open_to:   prev[dim].open_to,
          feedback: { ok: true, msg: "Saved" },
        },
      }));
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to save";
      setDimDrafts(prev => ({
        ...prev,
        [dim]: { ...prev[dim], saving: false, feedback: { ok: false, msg } },
      }));
    }
  };

  // ── Cost Centers state ───────────────────────────────────────────────────
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [ccDrafts, setCCDrafts] = useState<Record<string, CCDraft>>({});
  const [ccLoading, setCCLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [buFilter, setBuFilter] = useState("");

  // ── Temp password modal state ─────────────────────────────────────────────
  interface TempPwModal { cc: string; owner: string; email: string; password: string; }
  const [tempPwModal,   setTempPwModal]   = useState<TempPwModal | null>(null);
  const [tempPwSetting, setTempPwSetting] = useState(false);
  const [tempPwModalErr,setTempPwModalErr]= useState("");
  const [tempPwDoneMsg, setTempPwDoneMsg] = useState<string | null>(null);
  const tempPwDoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Add-form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addDraft, setAddDraft] = useState({ cost_center_number: "", cost_center_name: "", business_unit_id: null as number | null });
  const [addSaving, setAddSaving] = useState(false);
  const [addFeedback, setAddFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // BU & User options for dropdowns
  const [buOptions, setBuOptions] = useState<BUOption[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);

  const loadCostCenters = useCallback(() => {
    setCCLoading(true);
    api.get("/admin/cost-centers")
      .then(res => {
        const ccs: CostCenter[] = res.data;
        setCostCenters(ccs);
        const drafts: Record<string, CCDraft> = {};
        ccs.forEach(cc => {
          drafts[cc.cost_center_number] = {
            cost_center_number: cc.cost_center_number,
            cost_center_name:   cc.cost_center_name,
            business_unit:      cc.business_unit,
            business_unit_id:   cc.business_unit_id ?? null,
            editing: false, saving: false, resetting: false, feedback: null,
          };
        });
        setCCDrafts(drafts);
        setCCLoading(false);
      })
      .catch(() => setCCLoading(false));
  }, []);

  const loadBUOptions = useCallback(() => {
    api.get("/business-units").then(res => setBuOptions(res.data)).catch(() => {});
  }, []);

  const loadUserOptions = useCallback(() => {
    api.get("/users").then(res => setUserOptions(res.data)).catch(() => {});
  }, []);

  useEffect(() => { loadCostCenters(); loadBUOptions(); loadUserOptions(); }, [loadCostCenters, loadBUOptions, loadUserOptions]);

  const startEditingCC = (origCC: string) => {
    setCCDrafts(prev => ({ ...prev, [origCC]: { ...prev[origCC], editing: true, feedback: null } }));
  };

  const cancelEditingCC = (origCC: string) => {
    const orig = costCenters.find(c => c.cost_center_number === origCC);
    setCCDrafts(prev => ({
      ...prev,
      [origCC]: {
        ...prev[origCC],
        cost_center_number: origCC,
        cost_center_name:   orig?.cost_center_name  ?? prev[origCC].cost_center_name,
        business_unit:      orig?.business_unit      ?? prev[origCC].business_unit,
        business_unit_id:   orig?.business_unit_id  ?? prev[origCC].business_unit_id,
        editing: false, feedback: null,
      },
    }));
  };

  const updateCCField = (origCC: string, field: keyof CCDraft, val: any) => {
    setCCDrafts(prev => ({
      ...prev,
      [origCC]: { ...prev[origCC], [field]: val, feedback: null },
    }));
  };

  const saveCC = async (origCC: string) => {
    const draft = ccDrafts[origCC];
    setCCDrafts(prev => ({ ...prev, [origCC]: { ...prev[origCC], saving: true, feedback: null } }));
    const buName = buOptions.find(b => b.id === draft.business_unit_id)?.name || draft.business_unit;
    try {
      await api.put(`/admin/cost-centers/${origCC}`, {
        cost_center_number: draft.cost_center_number,
        cost_center_name:   draft.cost_center_name,
        business_unit:      buName,
        business_unit_id:   draft.business_unit_id,
      });
      loadCostCenters();
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to save";
      setCCDrafts(prev => ({
        ...prev,
        [origCC]: { ...prev[origCC], saving: false, feedback: { ok: false, msg } },
      }));
    }
  };

  const toggleCCActive = async (cc: string, currentlyDeactivated: boolean) => {
    try {
      if (currentlyDeactivated) {
        await api.post(`/admin/cost-centers/${cc}/reactivate`);
      } else {
        await api.post(`/admin/cost-centers/${cc}/deactivate`, { fiscal_year: currentFY });
      }
      loadCostCenters();
    } catch {
      // silently fail — row won't change
    }
  };

  const deleteCC = (cc: string) => {
    setDeleteConfirm({ type: "cc", key: cc });
    setDeletePw("");
    setDeletePwErr("");
  };

  const deleteAcct = (account: string) => {
    setDeleteConfirm({ type: "account", key: account });
    setDeletePw("");
    setDeletePwErr("");
  };

  const confirmDeleteAction = async () => {
    if (!deleteConfirm || !deletePw) return;
    setDeletePwBusy(true);
    setDeletePwErr("");
    try {
      await api.post("/admin/verify-password", { password: deletePw });
    } catch {
      setDeletePwErr("Incorrect password. Please try again.");
      setDeletePwBusy(false);
      return;
    }
    try {
      if (deleteConfirm.type === "cc") {
        await api.delete(`/admin/cost-centers/${deleteConfirm.key}`);
        loadCostCenters();
      } else {
        await api.delete(`/admin/accounts/${encodeURIComponent(deleteConfirm.key)}`);
        setAccounts(prev => prev.filter(a => a.account !== deleteConfirm.key));
        setAcctDrafts(prev => { const n = { ...prev }; delete n[deleteConfirm.key]; return n; });
      }
      setDeleteConfirm(null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Delete failed";
      setDeletePwErr(msg);
    } finally {
      setDeletePwBusy(false);
    }
  };

  const generateTempPw = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  };

  const openTempPwModal = (cc: string) => {
    const ccObj = costCenters.find(c => c.cost_center_number === cc);
    if (!ccObj) return;
    setTempPwModal({ cc, owner: ccObj.cost_center_name, email: ccObj.email || "", password: generateTempPw() });
    setTempPwModalErr("");
  };

  const doSetTempPassword = async () => {
    if (!tempPwModal) return;
    setTempPwSetting(true);
    setTempPwModalErr("");
    try {
      await api.post(`/admin/cost-centers/${tempPwModal.cc}/set-temp-password`, { temp_password: tempPwModal.password });
      const emailBody =
        `Subject: Temp QFT Password\n\n` +
        `Hello ${tempPwModal.owner},\n\n` +
        `Your temporary QFT password is: ${tempPwModal.password}\n\n` +
        `You will be prompted to create a new password the next time you log in. ` +
        `Please keep this password secure.\n\n` +
        `Best regards,\nQFT Admin`;
      navigator.clipboard.writeText(emailBody).catch(() => {});
      loadCostCenters();
      setTempPwModal(null);
      const doneMsg = tempPwModal.email
        ? `Email message copied to clipboard — send to ${tempPwModal.email} with subject: Temp QFT Password`
        : `Email message copied to clipboard.`;
      setTempPwDoneMsg(doneMsg);
      if (tempPwDoneTimer.current) clearTimeout(tempPwDoneTimer.current);
      tempPwDoneTimer.current = setTimeout(() => setTempPwDoneMsg(null), 6000);
    } catch (err: any) {
      setTempPwModalErr(err?.response?.data?.error || "Failed to set password. Please try again.");
    } finally {
      setTempPwSetting(false);
    }
  };

  const addCostCenter = async () => {
    if (!addDraft.cost_center_number || !addDraft.cost_center_name) {
      setAddFeedback({ ok: false, msg: "CC# and Name are required" });
      return;
    }
    setAddSaving(true);
    setAddFeedback(null);
    const buName = buOptions.find(b => b.id === addDraft.business_unit_id)?.name || "";
    try {
      await api.post("/admin/cost-centers", { ...addDraft, business_unit: buName });
      setShowAddForm(false);
      setAddDraft({ cost_center_number: "", cost_center_name: "", business_unit_id: null });
      loadCostCenters();
    } catch (err: any) {
      setAddFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to add" });
    } finally {
      setAddSaving(false);
    }
  };

  const allBUs = Array.from(new Set(costCenters.map(cc => cc.business_unit).filter(Boolean))).sort();

  const filteredCCs = costCenters.filter(cc => {
    const matchSearch = search === "" ||
      cc.cost_center_number.includes(search) ||
      cc.cost_center_name.toLowerCase().includes(search.toLowerCase());
    const matchBU = buFilter === "" || cc.business_unit === buFilter;
    return matchSearch && matchBU;
  });

  // ── Accounts state ───────────────────────────────────────────────────────
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [acctDrafts, setAcctDrafts] = useState<Record<string, AcctDraft>>({});
  const [acctLoading, setAcctLoading] = useState(false);
  const [acctSearch, setAcctSearch] = useState("");

  const loadAccounts = useCallback(() => {
    setAcctLoading(true);
    api.get("/admin/accounts")
      .then(res => {
        const accts: Account[] = res.data;
        setAccounts(accts);
        const drafts: Record<string, AcctDraft> = {};
        accts.forEach(a => {
          drafts[a.account] = { account: a.account, category: a.category, editing: false, saving: false, feedback: null };
        });
        setAcctDrafts(drafts);
        setAcctLoading(false);
      })
      .catch(() => setAcctLoading(false));
  }, []);

  // ── Unmapped accounts state ─────────────────────────────────────────────
  const [unmappedAccts, setUnmappedAccts] = useState<UnmappedAccount[]>([]);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [unmappedOpen, setUnmappedOpen] = useState(false);
  const [unmappedSelected, setUnmappedSelected] = useState<Set<number>>(new Set());
  const [unmappedAssigning, setUnmappedAssigning] = useState(false);

  const loadUnmapped = useCallback(() => {
    setUnmappedLoading(true);
    api.get("/admin/accounts/unmapped")
      .then(res => { setUnmappedAccts(res.data); setUnmappedLoading(false); })
      .catch(() => setUnmappedLoading(false));
  }, []);

  const toggleUnmappedSelect = (id: number) => {
    setUnmappedSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllUnmapped = () => {
    if (unmappedSelected.size === unmappedAccts.length) {
      setUnmappedSelected(new Set());
    } else {
      setUnmappedSelected(new Set(unmappedAccts.map(a => a.id)));
    }
  };

  const assignUnmappedToFY = async (fy: string) => {
    if (unmappedSelected.size === 0) return;
    setUnmappedAssigning(true);
    try {
      await api.post("/admin/accounts/assign-fy", { account_ids: Array.from(unmappedSelected), fiscal_year: fy });
      setUnmappedSelected(new Set());
      loadUnmapped();
      loadAccounts();
    } catch { /* ignore */ }
    setUnmappedAssigning(false);
  };

  const deleteUnmappedAccounts = async () => {
    if (unmappedSelected.size === 0) return;
    const count = unmappedSelected.size;
    if (!window.confirm(`Delete ${count} unmapped account${count > 1 ? "s" : ""} and all their budget/actuals data?`)) return;
    setUnmappedAssigning(true);
    let deleted = 0;
    for (const id of Array.from(unmappedSelected)) {
      const acct = unmappedAccts.find(a => a.id === id);
      if (!acct) continue;
      try {
        await api.delete(`/admin/accounts/${encodeURIComponent(acct.account)}`);
        deleted++;
      } catch { /* ignore */ }
    }
    setUnmappedSelected(new Set());
    loadUnmapped();
    loadAccounts();
    setUnmappedAssigning(false);
  };

  useEffect(() => {
    if (activeTab === "accounts" && accounts.length === 0) loadAccounts();
  }, [activeTab, accounts.length, loadAccounts]);

  useEffect(() => {
    if (activeTab === "accounts" && unmappedOpen && unmappedAccts.length === 0) loadUnmapped();
  }, [activeTab, unmappedOpen, unmappedAccts.length, loadUnmapped]);

  const startEditingAcct = (key: string) => {
    setAcctDrafts(prev => ({ ...prev, [key]: { ...prev[key], editing: true, feedback: null } }));
  };

  const cancelEditingAcct = (key: string) => {
    const orig = accounts.find(a => a.account === key);
    setAcctDrafts(prev => ({
      ...prev,
      [key]: { ...prev[key], account: orig?.account ?? prev[key].account, category: orig?.category ?? prev[key].category, editing: false, feedback: null },
    }));
  };

  const updateAcctField = (key: string, field: "account" | "category", val: string) => {
    setAcctDrafts(prev => ({ ...prev, [key]: { ...prev[key], [field]: val, feedback: null } }));
  };

  const saveAcct = async (origAccount: string) => {
    const draft = acctDrafts[origAccount];
    setAcctDrafts(prev => ({ ...prev, [origAccount]: { ...prev[origAccount], saving: true, feedback: null } }));
    try {
      await api.put("/admin/accounts/bulk", { old_account: origAccount, account: draft.account, category: draft.category });
      // Re-key drafts if name changed
      setAccounts(prev => prev.map(a => a.account === origAccount ? { ...a, account: draft.account, category: draft.category } : a));
      setAcctDrafts(prev => {
        const next = { ...prev };
        delete next[origAccount];
        const ccCount = accounts.find(a => a.account === origAccount)?.cc_count ?? 0;
        next[draft.account] = { ...draft, saving: false, editing: false, feedback: { ok: true, msg: `Saved — updated ${ccCount} CCs` } };
        return next;
      });
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to save";
      setAcctDrafts(prev => ({ ...prev, [origAccount]: { ...prev[origAccount], saving: false, feedback: { ok: false, msg } } }));
    }
  };

  const allCategories = Array.from(new Set(accounts.map(a => a.category))).sort();

  const ACCT_CATEGORY_ORDER = ["COMPENSATION","OCCUPANCY","OPERATING","ADVERTISING","ALLOCATED","AMORT","CORP_OH","OTHERADJUSTMENTS"];
  const filteredAccts = accounts.filter(a =>
    !acctSearch ||
    a.account.toLowerCase().includes(acctSearch.toLowerCase()) ||
    a.category.toLowerCase().includes(acctSearch.toLowerCase())
  );
  // Group filtered accounts by category in canonical order
  const acctsByCategory: { category: string; accounts: Account[] }[] = (() => {
    const groups: Record<string, Account[]> = {};
    filteredAccts.forEach(a => { (groups[a.category] = groups[a.category] || []).push(a); });
    const seen = new Set<string>();
    const ordered: { category: string; accounts: Account[] }[] = [];
    ACCT_CATEGORY_ORDER.forEach(cat => {
      const key = Object.keys(groups).find(k => k.toUpperCase() === cat);
      if (key) { ordered.push({ category: key, accounts: groups[key] }); seen.add(key); }
    });
    Object.keys(groups).filter(k => !seen.has(k)).forEach(k => ordered.push({ category: k, accounts: groups[k] }));
    return ordered;
  })();

  // ── Shared CCs-by-BU (loaded once, used by Dashboard + Forecast CC filters) ──
  const [ccsByBU, setCCsByBU] = useState<Record<string, { cost_center_number: string; cost_center_name: string }[]>>({});

  useEffect(() => {
    if (!currentFY) return;
    api.get("/admin/cost-centers-by-bu", { params: { fy: currentFY } })
      .then(res => setCCsByBU(res.data.by_bu || {}))
      .catch(() => {});
  }, [currentFY]);

  // ── Variance tab state ───────────────────────────────────────────────────
  interface VarRow { cost_center_number: string; cost_center_name: string; business_unit: string; dims: Record<string, number>; }
  const [varRows,        setVarRows]        = useState<VarRow[]>([]);
  const [varLoading,     setVarLoading]     = useState(false);
  const [varDrafts,      setVarDrafts]      = useState<Record<string, string>>({}); // key: "cc|dim"
  const [varSaving,      setVarSaving]      = useState<Record<string, boolean>>({}); // key: "cc|dim"
  const [varFeedback,    setVarFeedback]    = useState<Record<string, boolean>>({}); // key: "cc|dim"
  const [varEditingRows, setVarEditingRows] = useState<Record<string, boolean>>({}); // key: cc number
  const [varBU,          setVarBU]          = useState("");

  // Only edit the current forecast cycle dimension
  const varActiveDim = getDefaultDim();

  const loadVarRows = (bu = varBU) => {
    setVarLoading(true);
    const params = bu ? `?bu=${encodeURIComponent(bu)}` : "";
    api.get(`/admin/forecast-variance${params}`)
      .then(res => {
        const rows: VarRow[] = res.data;
        setVarRows(rows);
        const drafts: Record<string, string> = {};
        rows.forEach(r => {
          ["July Forecast", "October Forecast", "January Forecast"].forEach(dim => {
            drafts[`${r.cost_center_number}|${dim}`] = String(r.dims[dim] ?? 0);
          });
        });
        setVarDrafts(drafts);
        setVarFeedback({});
        setVarEditingRows({});
      })
      .catch(() => {})
      .finally(() => setVarLoading(false));
  };

  useEffect(() => {
    if (activeTab === "salary")  loadSalaryCfg();
    if (activeTab === "finance") { loadFinanceTargets(); loadVarRows(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Salary Config state ───────────────────────────────────────────────────
  const [salaryCfgFY,       setSalaryCfgFY]       = useState<string>(autoFY());
  const [salaryRaiseBefore, setSalaryRaiseBefore] = useState<string>("0");
  const [salaryRaiseAfter,  setSalaryRaiseAfter]  = useState<string>("3.5");
  const [salaryBipRaise,    setSalaryBipRaise]    = useState<string>("0");
  const [salaryCfgLoading,  setSalaryCfgLoading]  = useState(false);
  const [salaryCfgFeedback, setSalaryCfgFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [payrollTax, setPayrollTax] = useState<Record<string, string>>({
    apr:"8.50", may:"8.50", jun:"13.37", jul:"7.60", aug:"7.60", sep:"7.60",
    oct:"7.60", nov:"7.60", dec:"7.60", jan:"9.04", feb:"9.04", mar:"7.50",
  });
  const [insurancePerHead, setInsurancePerHead] = useState<string>("1003.33");
  const [retirementRate,   setRetirementRate]   = useState<string>("6.37");
  const PAYROLL_TAX_MONTHS = [
    { key:"apr", label:"Apr" }, { key:"may", label:"May" }, { key:"jun", label:"Jun" },
    { key:"jul", label:"Jul" }, { key:"aug", label:"Aug" }, { key:"sep", label:"Sep" },
    { key:"oct", label:"Oct" }, { key:"nov", label:"Nov" }, { key:"dec", label:"Dec" },
    { key:"jan", label:"Jan" }, { key:"feb", label:"Feb" }, { key:"mar", label:"Mar" },
  ];

  // ── Finance View state ────────────────────────────────────────────────────
  interface FinanceRow {
    cost_center_number:     string;
    cost_center_name:       string;
    cost_center_owner?:     string;
    business_unit:          string;
    dimension:              string;   // current forecast dim name
    annual_budget_dim:      string;   // e.g. "Annual Budget FY26"
    ref_dimension:          string;   // e.g. "October Forecast"
    dimension_total:        number;
    annual_budget_total:    number;
    ref_forecast_total:     number;
    july_av:                number;   // from forecast_variance
    october_av:             number;
    january_av:             number;   // editable — saves to forecast_variance
    adjusted_annual_budget: number;   // AB + July AV + Oct AV (read-only)
    met:                    boolean;
  }
  const [financeRows,      setFinanceRows]      = useState<FinanceRow[]>([]);
  const [financeLoading,   setFinanceLoading]   = useState(false);
  const [editingJanAV, setEditingJanAV] = useState<Record<string, string>>({});
  const [savingTarget, setSavingTarget] = useState<string | null>(null);
  const [downloadingCC,  setDownloadingCC]  = useState<string | null>(null);
  const [financeFilterBU, setFinanceFilterBU] = useState("");

  // ── Export modal state ─────────────────────────────────────────────────────
  const [exportModalCC, setExportModalCC] = useState<string | null>(null);
  const [exportFile, setExportFile] = useState<File | null>(null);
  const [exportPassword, setExportPassword] = useState("");
  const [exportNeedsPassword, setExportNeedsPassword] = useState(false);
  const [exportDimension, setExportDimension] = useState<string>("Annual Budget");
  const [exportValidating, setExportValidating] = useState(false);
  const [exportValidation, setExportValidation] = useState<{
    valid: boolean; ccMatch: boolean; fileCC: string | null;
    dimension: string; filename: string; fy: string;
    sheets: { salary: boolean; bip: boolean; plan: boolean; planSheetName: string | null; planSheets?: string[] };
    writeTargets?: { sheet: string; description: string }[];
    planMonthStartCol?: number;
    allSheets: string[]; counts: { employees: number; budgetAccounts: number };
  } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportPlanSheet, setExportPlanSheet] = useState<string>("");

  // ── Delete confirmation (password-protected) ─────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "cc" | "account"; key: string } | null>(null);
  const [deletePw,      setDeletePw]      = useState("");
  const [deletePwErr,   setDeletePwErr]   = useState("");
  const [deletePwBusy,  setDeletePwBusy]  = useState(false);

  const loadFinanceTargets = () => {
    setFinanceLoading(true);
    api.get("/admin/finance-targets")
      .then(r => setFinanceRows(r.data))
      .catch(() => {})
      .finally(() => setFinanceLoading(false));
  };

  // Save January Allowed Variance directly; Target is computed read-only = AAB + Jan AV
  const saveJanAV = (cc: string) => {
    const raw = editingJanAV[cc];
    if (raw === undefined) return;
    const newAV = parseFloat(raw);
    if (isNaN(newAV) || newAV < 0) { setEditingJanAV(e => { const n = { ...e }; delete n[cc]; return n; }); return; }
    setSavingTarget(cc);
    api.put("/admin/forecast-variance", { cost_center_number: cc, dimension: "January Forecast", variance_allowed: newAV })
      .then(() => {
        setFinanceRows(rows => rows.map(r =>
          r.cost_center_number === cc
            ? { ...r, january_av: newAV, met: r.dimension_total <= (r.adjusted_annual_budget + newAV) }
            : r
        ));
        setEditingJanAV(e => { const n = { ...e }; delete n[cc]; return n; });
      })
      .finally(() => setSavingTarget(null));
  };

  const handleFinanceDownload = (cc: string) => {
    setExportModalCC(cc);
    setExportFile(null);
    setExportPassword("");
    setExportNeedsPassword(false);
    setExportDimension("Annual Budget");
    setExportValidation(null);
    setExportError(null);
    setExporting(false);
  };

  const handleExportValidate = async (file: File, pw?: string) => {
    if (!exportModalCC) return;
    setExportFile(file);
    setExportValidating(true);
    setExportValidation(null);
    setExportError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("cost_center", exportModalCC);
      if (pw || exportPassword) form.append("password", pw || exportPassword);
      const res = await api.post("/export/validate-template", form);
      setExportValidation(res.data);
      setExportPlanSheet(res.data.sheets?.planSheetName || "");
      setExportNeedsPassword(false);
    } catch (err: any) {
      console.error("[Export validate]", err?.response?.status, err?.response?.data, err?.message);
      const respData = err?.response?.data;
      const status = err?.response?.status;
      if (respData?.needsPassword || status === 422) {
        setExportNeedsPassword(true);
        setExportError(null);
      } else if (!err?.response) {
        // Network error - backend not reachable
        setExportError("Cannot reach server. Please check the backend is running.");
      } else {
        setExportError(respData?.error || `Validation failed (${status})`);
      }
    } finally {
      setExportValidating(false);
    }
  };

  const handleExportPasswordSubmit = () => {
    if (!exportFile || !exportPassword) return;
    handleExportValidate(exportFile, exportPassword);
  };

  const handleExportDownload = async () => {
    if (!exportModalCC || !exportFile) return;
    setExporting(true);
    setExportError(null);
    try {
      const form = new FormData();
      form.append("file", exportFile);
      form.append("cost_center", exportModalCC);
      if (exportPassword) form.append("password", exportPassword);
      form.append("dimension", exportDimension);
      if (exportPlanSheet) form.append("planSheetOverride", exportPlanSheet);
      const res = await api.post("/export/populate-template", form, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${exportModalCC}_export.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setExportModalCC(null);
    } catch (err: any) {
      setExportError(err?.response?.data?.error || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const loadSalaryCfg = (fy?: string) => {
    const fyParam = fy || salaryCfgFY;
    setSalaryCfgLoading(true);
    setSalaryCfgFeedback(null);
    api.get("/admin/salary-config", { params: { fy: fyParam } })
      .then(r => {
        setSalaryRaiseBefore(((r.data.raise_before_m3 ?? 0) * 100).toFixed(2));
        setSalaryRaiseAfter(((r.data.raise_after_m3  ?? 0.035) * 100).toFixed(2));
        setSalaryBipRaise(((r.data.bip_raise         ?? 0) * 100).toFixed(2));
        setInsurancePerHead(((r.data.insurance_per_head ?? 1003.33)).toFixed(2));
        setRetirementRate(((r.data.retirement_rate ?? 0.0637) * 100).toFixed(4));
        if (r.data.payroll_tax) {
          const pt: Record<string, string> = {};
          Object.entries(r.data.payroll_tax).forEach(([k, v]) => {
            pt[k] = ((v as number) * 100).toFixed(2);
          });
          setPayrollTax(pt);
        }
      })
      .catch(() => {})
      .finally(() => setSalaryCfgLoading(false));
  };

  const saveSalaryCfg = () => {
    setSalaryCfgFeedback(null);
    const pt: Record<string, number> = {};
    Object.entries(payrollTax).forEach(([k, v]) => { pt[k] = parseFloat(v) / 100 || 0; });
    api.put("/admin/salary-config", {
      fy: salaryCfgFY,
      raise_before_m3:  parseFloat(salaryRaiseBefore) / 100 || 0,
      raise_after_m3:   parseFloat(salaryRaiseAfter)  / 100 || 0,
      bip_raise:        parseFloat(salaryBipRaise)    / 100 || 0,
      insurance_per_head: parseFloat(insurancePerHead) || 1003.33,
      retirement_rate:    parseFloat(retirementRate)   / 100 || 0.0637,
      payroll_tax: pt,
    })
      .then(() => setSalaryCfgFeedback({ ok: true, msg: `Saved for ${salaryCfgFY}.` }))
      .catch(() => setSalaryCfgFeedback({ ok: false, msg: "Save failed." }));
  };

  // ── Upload inventory state ────────────────────────────────────────────────
  interface ActualsInvRow { month: string; cc_count: number; last_upload: string; fiscal_year: string; }
  interface BudgetInvCC   { cost_center_number: string; cost_center_name: string; total_budget: number; }
  interface BudgetInvBU   { business_unit: string; ccs: BudgetInvCC[]; total: number; }
  interface BudgetInvDim  { dimension: string; business_units: BudgetInvBU[]; }

  const [actualsInv,    setActualsInv]    = useState<ActualsInvRow[]>([]);
  const [actualsInvFY,  setActualsInvFY]  = useState<string>("");
  const [actualsInvBU,  setActualsInvBU]  = useState<string>("");
  const [budgetInv,     setBudgetInv]     = useState<BudgetInvDim[]>([]);
  const [uploadInvFY,   setUploadInvFY]   = useState<string>("");
  const [uploadInvLoad, setUploadInvLoad] = useState(false);

  const FISCAL_MONTHS = ["April","May","June","July","August","September","October","November","December","January","February","March"];

  const getFiscalQuarter = (month: string): string => {
    const q1 = ["April", "May", "June"];
    const q2 = ["July", "August", "September"];
    const q3 = ["October", "November", "December"];
    const q4 = ["January", "February", "March"];
    if (q1.includes(month)) return "Q1";
    if (q2.includes(month)) return "Q2";
    if (q3.includes(month)) return "Q3";
    if (q4.includes(month)) return "Q4";
    return "YTD";
  };

  const refreshInventory = useCallback((bu?: string) => {
    const buParam = bu !== undefined ? bu : actualsInvBU;
    setUploadInvLoad(true);
    const buQuery = buParam ? `?bu=${encodeURIComponent(buParam)}` : "";
    Promise.all([
      api.get(`/admin/actuals-inventory${buQuery}`).then(r => {
        setActualsInv(r.data);
        const fys = Array.from(new Set<string>((r.data as ActualsInvRow[]).map(x => x.fiscal_year))).sort().reverse();
        if (fys.length > 0) setActualsInvFY(prev => prev || fys[0]);
      }).catch(() => {}),
      api.get("/admin/budget-inventory").then(r => {
        setBudgetInv(r.data);
        const fyDims = r.data.filter((d: BudgetInvDim) => /FY\d+/i.test(d.dimension));
        if (fyDims.length > 0) setUploadInvFY(prev => prev || fyDims[0].dimension);
      }).catch(() => {}),
    ]).finally(() => setUploadInvLoad(false));
  }, [actualsInvBU]);

  useEffect(() => {
    if (activeTab !== "upload") return;
    refreshInventory();
  }, [activeTab, refreshInventory]);

  const handleVarSaveRow = async (cc: string) => {
    const key = `${cc}|${varActiveDim}`;
    const val = parseFloat(varDrafts[key] ?? "0");
    const saved = varRows.find(r => r.cost_center_number === cc)?.dims[varActiveDim] ?? 0;
    const saves = (!isNaN(val) && val >= 0 && val !== saved)
      ? [{ dim: varActiveDim, val }]
      : [];

    if (saves.length === 0) {
      setVarEditingRows(r => { const n = { ...r }; delete n[cc]; return n; });
      return;
    }
    saves.forEach(s => setVarSaving(sv => ({ ...sv, [`${cc}|${s.dim}`]: true })));
    try {
      await Promise.all(saves.map(s =>
        api.put("/admin/forecast-variance", { cost_center_number: cc, dimension: s.dim, variance_allowed: s.val })
      ));
      setVarRows(rows => rows.map(r =>
        r.cost_center_number === cc
          ? { ...r, dims: { ...r.dims, ...Object.fromEntries(saves.map(s => [s.dim, s.val])) } }
          : r
      ));
      saves.forEach(s => setVarFeedback(f => ({ ...f, [`${cc}|${s.dim}`]: true })));
      setTimeout(() => saves.forEach(s =>
        setVarFeedback(f => { const n = { ...f }; delete n[`${cc}|${s.dim}`]; return n; })
      ), 2000);
      setVarEditingRows(r => { const n = { ...r }; delete n[cc]; return n; });
    } catch { /* keep editing */ }
    finally { saves.forEach(s => setVarSaving(sv => { const n = { ...sv }; delete n[`${cc}|${s.dim}`]; return n; })); }
  };

  // ── BU Dashboard state ──────────────────────────────────────────────────
  const [dashMonth, setDashMonth] = useState<string>("");
  const [dashAvailableMonths, setDashAvailableMonths] = useState<string[]>([]);
  const [dashBU, setDashBU] = useState<string>("");
  const [dashSelectedCCs, setDashSelectedCCs] = useState<string[]>([]);
  const [dashBusinessUnits, setDashBusinessUnits] = useState<string[]>([]);
  const [dashBUSummary, setDashBUSummary] = useState<BUSummaryRow[]>([]);
  const [dashData, setDashData] = useState<CategoryKPI[]>([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashMonthsLoading, setDashMonthsLoading] = useState(false);
  const [drillCC, setDrillCC] = useState<string | null>(null);
  const [drillCategory, setDrillCategory] = useState<string | null>(null);
  const [drillData, setDrillData] = useState<AcctRow[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillTitle, setDrillTitle] = useState("");
  const [buChartVisible, setBuChartVisible] = useState(false);
  const [buChartData, setBuChartData] = useState<MonthlyTrendPoint[] | null>(null);
  const [buChartLoading, setBuChartLoading] = useState(false);
  const [buChartError, setBuChartError] = useState<string | null>(null);
  const [buChartHoverIdx, setBuChartHoverIdx] = useState<number | null>(null);
  const [dashMonthlyTrend, setDashMonthlyTrend] = useState<MonthlyTrendPoint[] | null>(null);
  const [dashAnnualBudgetTotal, setDashAnnualBudgetTotal] = useState<number>(0);
  const [dashCCSummary, setDashCCSummary] = useState<{cost_center: string; cost_center_name: string; business_unit: string; month_budget: number; month_actual: number; ytd_budget: number; ytd_actual: number; month_variance: number; ytd_variance: number; categories: CCCatBreakdown[]}[]>([]);
  const [dashChartFilterCC, setDashChartFilterCC] = useState<string | null>(null);
  const [expandedCCs, setExpandedCCs] = useState<Set<string>>(new Set());

  const doLoadDashboard = useCallback((month: string, bu: string, selectedCCs: string[] = [], ytd = false) => {
    if (!month) return;
    setDashLoading(true);
    setDashData([]);
    setDashBUSummary([]);
    setDrillData(null);
    setDrillCC(null);
    setDrillCategory(null);
    setExpandedCCs(new Set());
    const params = new URLSearchParams({ month });
    if (bu) params.set("bu", bu);
    if (selectedCCs.length > 0) params.set("cc", selectedCCs.join(","));
    if (currentFY) params.set("fiscalYear", currentFY);
    if (ytd) params.set("ytd", "1");
    api.get(`/admin/bu-dashboard?${params}`)
      .then(res => {
        setDashData(res.data.categories || []);
        setDashBusinessUnits(res.data.business_units || []);
        setDashBUSummary(res.data.bu_summary || []);
        setDashCCSummary(res.data.cc_summary || []);
        setDashLoading(false);
      })
      .catch(() => setDashLoading(false));
    // Fetch monthly trend in parallel for projection KPIs (no month/ytd filter needed)
    const trendParams = new URLSearchParams();
    if (bu) trendParams.set("bu", bu);
    if (selectedCCs.length > 0) trendParams.set("cc", selectedCCs.join(","));
    if (currentFY) trendParams.set("fiscalYear", currentFY);
    api.get(`/admin/bu-dashboard/monthly-trend?${trendParams}`)
      .then(res => {
        setDashMonthlyTrend(res.data.months || []);
        setDashAnnualBudgetTotal(res.data.annual_budget_total || 0);
      })
      .catch(() => { setDashMonthlyTrend(null); setDashAnnualBudgetTotal(0); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFY]);

  const doLoadAvailableMonths = useCallback((bu: string) => {
    setDashMonthsLoading(true);
    const params = new URLSearchParams();
    if (bu) params.set("bu", bu);
    if (currentFY) params.set("fy", currentFY);
    api.get(`/admin/bu-dashboard/available-months?${params}`)
      .then(res => {
        const months: string[] = res.data.months || [];
        setDashAvailableMonths(months);
        // pick latest month as default
        const latest = months[months.length - 1] || "";
        setDashMonth(latest);
        setDashMonthsLoading(false);
        if (latest) doLoadDashboard(latest, bu);
      })
      .catch(() => setDashMonthsLoading(false));
  }, [doLoadDashboard, currentFY]);

  useEffect(() => {
    if (activeTab === "dashboard" && fyLoaded && dashAvailableMonths.length === 0 && !dashMonthsLoading) {
      doLoadAvailableMonths(dashBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fyLoaded]);

  // ── Projected Year-End: YTD Actuals + remaining months' budget ──────────────
  // Uses actual spend for uploaded months, then fills remaining months with
  // their budget values. As actuals replace budget month by month, the
  // projection becomes increasingly grounded in real data.
  const dashProjection = useMemo(() => {
    if (!dashMonthlyTrend || dashMonthlyTrend.length === 0) return null;
    const forecastTotal = dashMonthlyTrend.reduce((s, p) => s + (p.budget || 0), 0);
    const actMonths = dashMonthlyTrend.filter(p => p.hasActuals);
    const ytdActualTotal = actMonths.reduce((s, p) => s + (p.actual || 0), 0);
    const n = actMonths.length;
    const remaining = dashMonthlyTrend.filter(p => !p.hasActuals);
    const remainingBudgetTotal = remaining.reduce((s, p) => s + (p.budget || 0), 0);

    // Projected Year-End = YTD Actuals + reference budget for remaining months
    const projectedYearEnd = n === 0 ? forecastTotal : ytdActualTotal + remainingBudgetTotal;

    return {
      forecastTotal,
      ytdActualTotal,
      annualBudgetTotal: dashAnnualBudgetTotal,
      projectedYearEnd: Math.round(projectedYearEnd),
      projectionVariance: Math.round(projectedYearEnd - forecastTotal),
      budgetVariance: Math.round(projectedYearEnd - forecastTotal),
      actualMonths: n,
      remainingMonths: remaining.length,
    };
  }, [dashMonthlyTrend, dashAnnualBudgetTotal]);

  const loadBuChart = useCallback((bu: string, ccs: string[], category: string | null, filterCC: string | null = null) => {
    setBuChartLoading(true);
    setBuChartError(null);
    const params = new URLSearchParams();
    if (currentFY) params.set("fiscalYear", currentFY);
    if (bu) params.set("bu", bu);
    // If a CC is selected from the offenders chart, use that; otherwise use the pill filter
    const effectiveCCs = filterCC ? [filterCC] : ccs;
    if (effectiveCCs.length > 0) params.set("cc", effectiveCCs.join(","));
    if (category) params.set("category", category);
    api.get(`/admin/bu-dashboard/monthly-trend?${params}`)
      .then(res => { setBuChartData(res.data.months || []); setBuChartLoading(false); })
      .catch((err) => { setBuChartError(err?.response?.data?.error || "Failed to load chart data"); setBuChartLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFY]);

  useEffect(() => {
    if (buChartVisible) loadBuChart(dashBU, dashSelectedCCs, drillCategory, dashChartFilterCC);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buChartVisible, dashBU, dashSelectedCCs, drillCategory, currentFY, dashChartFilterCC]);

  const doLoadDrilldown = (category: string, cc?: string) => {
    // Toggle off: clicking the same category+CC when it's already active
    if (drillCategory === category && ((!cc && !drillCC) || (cc && drillCC === cc))) {
      setDrillData(null);
      setDrillCC(null);
      setDrillCategory(null);
      return;
    }
    const catData = dashData.find(c => c.category === category);
    const ccRow = catData?.top_over.find(r => r.cost_center === cc)
               || catData?.top_under.find(r => r.cost_center === cc);
    const title = cc
      ? `${category} — CC ${cc}${ccRow?.cost_center_name ? ` (${ccRow.cost_center_name})` : ""}`
      : `${category} — All Cost Centers`;
    setDrillCategory(category);
    setDrillCC(cc || null);
    setDrillTitle(title);
    setDrillLoading(true);
    setDrillData([]);
    const effectiveMon = dashMonth === "YTD" ? dashAvailableMonths[dashAvailableMonths.length - 1] : dashMonth;
    const params = new URLSearchParams({ category, month: effectiveMon });
    if (cc) params.set("cc", cc);
    if (dashMonth === "YTD") params.set("ytd", "1");
    api.get(`/admin/bu-dashboard/cc-detail?${params}`)
      .then(res => { setDrillData(res.data.accounts || []); setDrillLoading(false); })
      .catch(() => setDrillLoading(false));
  };

  // ── Variance Report state ─────────────────────────────────────────────────
  const [vrData, setVrData] = useState<VRCostCenter[]>([]);
  const [vrLoading, setVrLoading] = useState(false);
  const [vrMonth, setVrMonth] = useState<string>("");
  const [vrBU, setVrBU] = useState<string>("");
  const [vrAvailableMonths, setVrAvailableMonths] = useState<string[]>([]);
  const [vrBusinessUnits, setVrBusinessUnits] = useState<string[]>([]);
  const [vrMonthsLoading, setVrMonthsLoading] = useState(false);
  const [vrActionPlans, setVrActionPlans] = useState<Record<string, VRActionPlanItem[]>>({});

  // Actions Report modal
  const [vrArOpen, setVrArOpen] = useState(false);
  const [vrArData, setVrArData] = useState<{ summary: { total: number; open: number; closed: number }; by_month: { month: string; open: number; closed: number }[]; items: { id: number; cost_center: string; cost_center_name: string; business_unit: string; cost_center_owner: string | null; month: string; action_text: string; category: string | null; status: "OPEN" | "CLOSED"; created_by: string | null }[] } | null>(null);
  const [vrArLoading, setVrArLoading] = useState(false);
  const [vrArFilter, setVrArFilter] = useState<"ALL" | "OPEN" | "CLOSED">("ALL");
  const [vrArCopied, setVrArCopied] = useState(false);

  const handleActionsReport = async () => {
    setVrArOpen(true);
    setVrArFilter("ALL");
    setVrArLoading(true);
    setVrArCopied(false);
    try {
      const params: Record<string, string> = { fy: currentFY };
      if (vrBU && vrData.length > 0) {
        const ccs = vrData.filter(d => d.business_unit === vrBU).map(d => d.cost_center);
        if (ccs.length > 0) params.cost_centers = ccs.join(",");
      }
      const res = await api.get("/action-plans/report", { params });
      setVrArData(res.data);
    } catch (err) {
      console.error("Failed to load actions report:", err);
    } finally {
      setVrArLoading(false);
    }
  };

  const handleArCopy = () => {
    if (!vrArData) return;
    const { summary, items } = vrArData;
    const filtered = vrArFilter === "ALL" ? items : items.filter(i => i.status === vrArFilter);
    let text = `Actions Report — ${currentFY}${vrBU ? ` — ${vrBU}` : ""}\n`;
    text += `Total: ${summary.total} | Open: ${summary.open} | Closed: ${summary.closed}\n`;
    text += `Filter: ${vrArFilter === "ALL" ? "All" : vrArFilter}\n\n`;
    text += "Month".padEnd(12) + "CC".padEnd(8) + "CC Name".padEnd(22) + "Owner".padEnd(18) + "Action".padEnd(40) + "Status\n";
    text += "-".repeat(106) + "\n";
    filtered.forEach(i => {
      text += (i.month || "").padEnd(12) + (i.cost_center || "").padEnd(8) + (i.cost_center_name || "").substring(0, 20).padEnd(22) + (i.cost_center_owner || "—").substring(0, 16).padEnd(18) + (i.action_text || "(no description)").substring(0, 38).padEnd(40) + i.status + "\n";
    });
    navigator.clipboard.writeText(text).then(() => {
      setVrArCopied(true);
      setTimeout(() => setVrArCopied(false), 2000);
    });
  };

  const doLoadVRData = useCallback((month: string, bu: string, availMonths?: string[]) => {
    if (!month) return;
    setVrLoading(true);
    const isYTD = month === "YTD";
    const effectiveMonths = availMonths || vrAvailableMonths;
    const effectiveMonth = isYTD ? effectiveMonths[effectiveMonths.length - 1] || "" : month;
    const params = new URLSearchParams({ month: effectiveMonth });
    if (bu) params.set("bu", bu);
    if (currentFY) params.set("fiscalYear", currentFY);
    if (isYTD) params.set("ytd", "1");
    api.get(`/admin/bu-dashboard/variance-report?${params}`)
      .then(res => {
        const ccs: VRCostCenter[] = res.data.cost_centers || [];
        setVrData(ccs);
        setVrBusinessUnits(res.data.business_units || []);
        setVrLoading(false);
        // Bulk-fetch action plans for all CCs in the report
        const ccNums = ccs.map(c => c.cost_center).filter(Boolean);
        if (ccNums.length > 0 && currentFY && effectiveMonth) {
          api.get("/action-plans/bulk", { params: { cost_centers: ccNums.join(","), fy: currentFY, month: effectiveMonth } })
            .then(apRes => setVrActionPlans(apRes.data || {}))
            .catch(() => setVrActionPlans({}));
        } else {
          setVrActionPlans({});
        }
      })
      .catch(() => { setVrLoading(false); setVrActionPlans({}); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFY, vrAvailableMonths]);

  const doLoadVRMonths = useCallback((bu: string) => {
    setVrMonthsLoading(true);
    const params = new URLSearchParams();
    if (bu) params.set("bu", bu);
    if (currentFY) params.set("fy", currentFY);
    api.get(`/admin/bu-dashboard/available-months?${params}`)
      .then(res => {
        const months: string[] = res.data.months || [];
        setVrAvailableMonths(months);
        const latest = months[months.length - 1] || "";
        setVrMonth(latest);
        setVrMonthsLoading(false);
        if (latest) doLoadVRData(latest, bu, months);
      })
      .catch(() => setVrMonthsLoading(false));
  }, [doLoadVRData, currentFY]);

  useEffect(() => {
    if (activeTab === "variancereport" && fyLoaded && vrAvailableMonths.length === 0 && !vrMonthsLoading) {
      doLoadVRMonths(vrBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fyLoaded]);

  const handleVRPrint = () => window.print();

  // ── BU Forecast state ───────────────────────────────────────────────────
  const [fcDimA, setFcDimA] = useState<string>("Annual Budget");
  const [fcDimB, setFcDimB] = useState<string>("July Forecast");
  const [fcFYA,  setFcFYA]  = useState<string>(autoFY());
  const [fcFYB,  setFcFYB]  = useState<string>(autoFY());
  const [fcBU,   setFcBU]   = useState<string>("");
  const [fcSelectedCCs, setFcSelectedCCs] = useState<string[]>([]);
  const [fcBusinessUnits, setFcBusinessUnits] = useState<string[]>([]);
  const [fcData,         setFcData]         = useState<ForecastCatKPI[]>([]);
  const [fcBUSummary,    setFcBUSummary]    = useState<ForecastBUSummaryRow[]>([]);
  const [fcLoading,      setFcLoading]      = useState(false);
  const [fcDrillCategory, setFcDrillCategory] = useState<string | null>(null);

  /** Build the full dimension string for the API (always appends FY). */
  const buildDimName = (dim: string, fy: string) => `${dim} ${fy}`;

  const doLoadForecast = useCallback((dimA: string, dimB: string, bu: string, selectedCCs: string[] = [], fyA?: string, fyB?: string) => {
    setFcLoading(true);
    setFcData([]);
    setFcBUSummary([]);
    setFcDrillCategory(null);
    const fullDimA = buildDimName(dimA, fyA ?? fcFYA);
    const fullDimB = buildDimName(dimB, fyB ?? fcFYB);
    const params = new URLSearchParams({ dimA: fullDimA, dimB: fullDimB });
    if (bu) params.set("bu", bu);
    if (selectedCCs.length > 0) params.set("cc", selectedCCs.join(","));
    api.get(`/admin/bu-forecast?${params}`)
      .then(res => {
        setFcData(res.data.categories || []);
        setFcBUSummary(res.data.bu_summary || []);
        setFcBusinessUnits(res.data.business_units || []);
        setFcLoading(false);
      })
      .catch(() => setFcLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fcFYA, fcFYB]);

  useEffect(() => {
    if (activeTab === "buforecast" && fcData.length === 0 && !fcLoading) {
      doLoadForecast(fcDimA, fcDimB, fcBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Actuals Dashboard state ──────────────────────────────────────────────
  const [actualsBU, setActualsBU] = useState<string>("");
  const [actualsData, setActualsData] = useState<ActualsCatKPI[]>([]);
  const [actualsBusinessUnits, setActualsBusinessUnits] = useState<string[]>([]);
  const [actualsLoading, setActualsLoading] = useState(false);
  const [actDrillCC, setActDrillCC] = useState<string | null>(null);
  const [actDrillCategory, setActDrillCategory] = useState<string | null>(null);
  const [actDrillData, setActDrillData] = useState<ActualsAcctRow[] | null>(null);
  const [actDrillLoading, setActDrillLoading] = useState(false);
  const [actDrillTitle, setActDrillTitle] = useState("");

  const doLoadActuals = useCallback((bu: string) => {
    setActualsLoading(true);
    setActualsData([]);
    setActDrillData(null);
    setActDrillCC(null);
    setActDrillCategory(null);
    const qs = bu ? `?bu=${encodeURIComponent(bu)}` : "";
    api.get(`/admin/actuals-dashboard${qs}`)
      .then(res => {
        setActualsData(res.data.categories || []);
        setActualsBusinessUnits(res.data.business_units || []);
        setActualsLoading(false);
      })
      .catch(() => setActualsLoading(false));
  }, []);


  const doLoadActualsDrilldown = (category: string, cc?: string) => {
    const catData = actualsData.find(c => c.category === category);
    const ccRow = catData?.top_spenders.find(r => r.cost_center === cc);
    const title = cc
      ? `${category} — CC ${cc}${ccRow?.cost_center_name ? ` (${ccRow.cost_center_name})` : ""}`
      : `${category} — All Cost Centers`;
    setActDrillCategory(category);
    setActDrillCC(cc || null);
    setActDrillTitle(title);
    setActDrillLoading(true);
    setActDrillData([]);
    const params = new URLSearchParams({ category });
    if (cc) params.set("cc", cc);
    api.get(`/admin/actuals-dashboard/cc-detail?${params}`)
      .then(res => { setActDrillData(res.data.accounts || []); setActDrillLoading(false); })
      .catch(() => setActDrillLoading(false));
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="admin-panel">

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div className="admin-tabs">
        {isAdminSetupGroup ? (
          <>
            <button className={`admin-tab${activeTab === "businessunits" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("businessunits")}>
              <span className="material-icons">business</span> Business Units
            </button>
            <button className={`admin-tab${activeTab === "costcenters" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("costcenters")}>
              <span className="material-icons">store</span> Cost Centers
            </button>
            <button className={`admin-tab${activeTab === "users" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("users")}>
              <span className="material-icons">people</span> Users
            </button>
            <button className={`admin-tab${activeTab === "accounts" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("accounts")}>
              <span className="material-icons">category</span> Accounts
            </button>
          </>
        ) : (
          <>
            <button className={`admin-tab${activeTab === "dashboard" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("dashboard")}>
              <span className="material-icons">bar_chart</span> Actuals Dashboard
            </button>
            <button className={`admin-tab${activeTab === "variancereport" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("variancereport")}>
              <span className="material-icons">assessment</span> Variance Report
            </button>
            <div className="admin-tab-divider" />
            <button className={`admin-tab${activeTab === "finance" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("finance")}>
              <span className="material-icons">savings</span> Forecast Status
            </button>
            <button className={`admin-tab${activeTab === "buforecast" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("buforecast")}>
              <span className="material-icons">compare_arrows</span> Forecast Comparison
            </button>
            <button className={`admin-tab${activeTab === "forecast" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("forecast")}>
              <span className="material-icons">date_range</span> Forecast Windows
            </button>
            <div className="admin-tab-divider" />
            <button className={`admin-tab${activeTab === "upload" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("upload")}>
              <span className="material-icons">upload_file</span> Data Upload
            </button>
            <button className={`admin-tab${activeTab === "salary" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("salary")}>
              <span className="material-icons">payments</span> Salary Config
            </button>
          </>
        )}
      </div>

      {/* ── Tab: Forecast Windows ────────────────────────────────────────── */}
      {activeTab === "forecast" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">event</span>
            <span className="admin-section-title">Forecast Dimension Windows</span>
            <span className="admin-section-subtitle">
              Select the active planning cycle · Edit dates for reference
            </span>
          </div>

          {/* ── Active Fiscal Year selector ── */}
          <div className="admin-forecast-bar admin-fy-bar">
            <span className="admin-forecast-bar-label">Active Fiscal Year</span>
            <select
              className="admin-fy-select"
              value={fyDraft}
              onChange={e => { setFyDraft(e.target.value); setFyFeedback(null); }}
            >
              {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
            <button
              className={`admin-fy-btn${fyLoading ? " admin-fy-btn--loading" : ""}`}
              onClick={handleSaveFY}
              disabled={fyLoading || fyDraft === currentFY}
            >
              {fyLoading ? "Saving…" : fyDraft === currentFY ? "Active" : "Set"}
            </button>
            {fyFeedback && (
              <span className={`admin-fy-feedback${fyFeedback.ok ? "" : " admin-fy-feedback--err"}`}>
                {fyFeedback.msg}
              </span>
            )}
          </div>

          {/* ── Password Reset Email ── */}
          <div className="admin-forecast-bar">
            <span className="admin-forecast-bar-label">Password Reset Email</span>
            <input
              className="admin-text-input"
              type="email"
              placeholder="firstname.last@daikinapplied.com"
              style={{ width: "260px" }}
              value={resetEmailDraft}
              onChange={e => { setResetEmailDraft(e.target.value); setResetEmailFeedback(null); }}
            />
            <button
              className={`admin-fy-btn${resetEmailLoading ? " admin-fy-btn--loading" : ""}`}
              onClick={handleSaveResetEmail}
              disabled={resetEmailLoading || resetEmailDraft === resetEmail || !resetEmailDraft}
            >
              {resetEmailLoading ? "Saving…" : resetEmailDraft === resetEmail && resetEmail ? "Saved" : "Save"}
            </button>
            {resetEmailFeedback && (
              <span className={`admin-fy-feedback${resetEmailFeedback.ok ? "" : " admin-fy-feedback--err"}`}>
                {resetEmailFeedback.msg}
              </span>
            )}
          </div>

          {/* ── Main Forecast Planning toggle ── */}
          <div className="admin-forecast-bar">
            <span className="admin-forecast-bar-label">Forecast Planning</span>
            <button
              className={`admin-toggle${forecastActive ? " admin-toggle--on" : ""}`}
              onClick={handleToggleForecast}
              title={forecastActive ? "Disable forecast planning" : "Enable forecast planning"}
            />
            <span className={`admin-forecast-status${forecastActive ? " admin-forecast-status--on" : ""}`}>
              {forecastActive ? "Active — users can edit the selected dimension" : "Inactive — all dim-switcher pills are disabled"}
            </span>
          </div>

          {/* ── Help Page URLs ── */}
          <div className="admin-forecast-bar" style={{ flexWrap: "wrap", gap: "8px 12px" }}>
            <span className="admin-forecast-bar-label" style={{ width: "100%" }}>Help Page URLs</span>
            <label style={{ fontSize: "12px", color: "#64748b", minWidth: "80px" }}>Admin URL</label>
            <input
              className="admin-text-input"
              type="url"
              placeholder="https://sharepoint.com/admin-help"
              style={{ width: "340px" }}
              value={helpUrlAdminDraft}
              onChange={e => { setHelpUrlAdminDraft(e.target.value); setHelpUrlFeedback(null); }}
            />
            <label style={{ fontSize: "12px", color: "#64748b", minWidth: "80px" }}>User URL</label>
            <input
              className="admin-text-input"
              type="url"
              placeholder="https://sharepoint.com/user-help"
              style={{ width: "340px" }}
              value={helpUrlUserDraft}
              onChange={e => { setHelpUrlUserDraft(e.target.value); setHelpUrlFeedback(null); }}
            />
            <button
              className={`admin-fy-btn${helpUrlLoading ? " admin-fy-btn--loading" : ""}`}
              onClick={handleSaveHelpUrls}
              disabled={helpUrlLoading || (helpUrlAdminDraft === helpUrlAdmin && helpUrlUserDraft === helpUrlUser)}
            >
              {helpUrlLoading ? "Saving…" : (helpUrlAdminDraft === helpUrlAdmin && helpUrlUserDraft === helpUrlUser) ? "Saved" : "Save"}
            </button>
            {helpUrlFeedback && (
              <span className={`admin-fy-feedback${helpUrlFeedback.ok ? "" : " admin-fy-feedback--err"}`}>
                {helpUrlFeedback.msg}
              </span>
            )}
          </div>

          <div className="admin-section-body" style={{ padding: 0 }}>
            {dimsLoading ? (
              <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
            ) : (
              <table className="admin-dim-table">
                <thead>
                  <tr>
                    <th style={{ width: "48px", textAlign: "center" }}>Select</th>
                    <th style={{ width: "28%" }}>Dimension</th>
                    <th style={{ width: "22%" }}>Open From</th>
                    <th style={{ width: "22%" }}>Open To</th>
                    <th style={{ width: "180px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_DIMS.map(dim => {
                    const draft = dimDrafts[dim];
                    if (!draft) return null;
                    const isSelected = selectedDim === dim;
                    return (
                      <tr key={dim} className={isSelected && forecastActive ? "admin-dim-row--live" : ""}>
                        {/* Radio */}
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="radio"
                            name="forecast-dim"
                            className="admin-radio"
                            checked={isSelected}
                            onChange={() => handleSelectDim(dim)}
                            title={`Set ${dim} as active planning cycle`}
                          />
                        </td>
                        {/* Dimension name */}
                        <td>
                          <span className="admin-dim-name">{dim}</span>
                          {isSelected && forecastActive && (
                            <span className="admin-dim-live-badge">Live</span>
                          )}
                          {isSelected && !forecastActive && (
                            <span className="admin-dim-selected-badge">Selected</span>
                          )}
                        </td>
                        {/* Open From */}
                        <td>
                          {draft.editing ? (
                            <input
                              type="date"
                              className="admin-date-input"
                              value={draft.open_from}
                              onChange={e => updateDimField(dim, "open_from", e.target.value)}
                            />
                          ) : (
                            <span className="admin-date-text">{fmtDate(draft.open_from)}</span>
                          )}
                        </td>
                        {/* Open To */}
                        <td>
                          {draft.editing ? (
                            <input
                              type="date"
                              className="admin-date-input"
                              value={draft.open_to}
                              onChange={e => updateDimField(dim, "open_to", e.target.value)}
                            />
                          ) : (
                            <span className="admin-date-text">{fmtDate(draft.open_to)}</span>
                          )}
                        </td>
                        {/* Actions */}
                        <td>
                          <div className="admin-btn-row">
                            {draft.editing ? (
                              <>
                                <button
                                  className="admin-btn admin-btn--primary"
                                  onClick={() => saveDimWindow(dim)}
                                  disabled={draft.saving || !draft.open_from || !draft.open_to}
                                >
                                  <span className="material-icons">save</span>
                                  {draft.saving ? "Saving…" : "Save"}
                                </button>
                                <button
                                  className="admin-btn admin-btn--ghost"
                                  onClick={() => cancelEditing(dim)}
                                  disabled={draft.saving}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className="admin-btn admin-btn--ghost"
                                onClick={() => startEditing(dim)}
                              >
                                <span className="material-icons">edit</span>
                                Edit
                              </button>
                            )}
                          </div>
                          {draft.feedback && (
                            <div className={`admin-toast admin-toast--${draft.feedback.ok ? "ok" : "err"}`} style={{ marginTop: "4px" }}>
                              <span className="material-icons" style={{ fontSize: "13px" }}>
                                {draft.feedback.ok ? "check_circle" : "error"}
                              </span>
                              {draft.feedback.msg}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Cost Centers ────────────────────────────────────────────── */}
      {activeTab === "costcenters" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">store</span>
            <span className="admin-section-title">Cost Centers</span>
            <span className="admin-kpi-chip">
              <span className="material-icons">groups</span>
              {costCenters.length} Cost Centers
            </span>
          </div>
          <div className="admin-section-body">

            {/* ── Toolbar ── */}
            <div className="admin-cc-toolbar">
              <div className="admin-search-wrap">
                <span className="material-icons">search</span>
                <input
                  className="admin-search"
                  type="text"
                  placeholder="Search by number or name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              {allBUs.length > 1 && (
                <select
                  className="admin-select"
                  value={buFilter}
                  onChange={e => setBuFilter(e.target.value)}
                >
                  <option value="">All Business Units</option>
                  {allBUs.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                </select>
              )}
              <button
                className="admin-btn admin-btn--primary"
                onClick={() => { setShowAddForm(f => !f); setAddFeedback(null); }}
              >
                <span className="material-icons">{showAddForm ? "close" : "add"}</span>
                {showAddForm ? "Cancel" : "Add Cost Center"}
              </button>
            </div>

            {/* ── Add-form ── */}
            {showAddForm && (
              <div className="admin-add-form">
                <input
                  className="admin-text-input"
                  placeholder="CC #"
                  style={{ width: "80px" }}
                  value={addDraft.cost_center_number}
                  onChange={e => setAddDraft(d => ({ ...d, cost_center_number: e.target.value }))}
                />
                <input
                  className="admin-text-input"
                  placeholder="Cost Center Name"
                  style={{ flex: 1 }}
                  value={addDraft.cost_center_name}
                  onChange={e => setAddDraft(d => ({ ...d, cost_center_name: e.target.value }))}
                />
                <select
                  className="admin-select"
                  value={addDraft.business_unit_id ?? ""}
                  onChange={e => setAddDraft(d => ({ ...d, business_unit_id: e.target.value ? Number(e.target.value) : null }))}
                  style={{ width: "180px" }}
                >
                  <option value="">Select Business Unit…</option>
                  {buOptions.map(bu => <option key={bu.id} value={bu.id}>{bu.name}</option>)}
                </select>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={addCostCenter}
                  disabled={addSaving}
                >
                  <span className="material-icons">add</span>
                  {addSaving ? "Adding…" : "Add"}
                </button>
                {addFeedback && (
                  <span className={`admin-toast admin-toast--${addFeedback.ok ? "ok" : "err"}`}>
                    <span className="material-icons" style={{ fontSize: "13px" }}>
                      {addFeedback.ok ? "check_circle" : "error"}
                    </span>
                    {addFeedback.msg}
                  </span>
                )}
              </div>
            )}

            {ccLoading ? (
              <div style={{ color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="admin-cc-table">
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "38%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>CC #</th>
                      <th>Name</th>
                      <th>Business Unit</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCCs.map(cc => {
                      const origCC = cc.cost_center_number;
                      const draft = ccDrafts[origCC];
                      if (!draft) return null;
                      const isInactive = !!cc.deactivated_from_fy;
                      return (
                        <tr key={origCC} className={isInactive ? "cc-row--inactive" : ""}>
                          {/* CC # */}
                          <td>
                            {draft.editing ? (
                              <input
                                className="admin-text-input"
                                style={{ width: "70px" }}
                                value={draft.cost_center_number}
                                onChange={e => updateCCField(origCC, "cost_center_number", e.target.value)}
                              />
                            ) : (
                              <span className="admin-cc-num">{origCC}</span>
                            )}
                          </td>
                          {/* Name */}
                          <td>
                            {draft.editing ? (
                              <input
                                className="admin-text-input"
                                style={{ width: "100%" }}
                                value={draft.cost_center_name}
                                onChange={e => updateCCField(origCC, "cost_center_name", e.target.value)}
                              />
                            ) : (
                              <span style={{ fontSize: "13px", color: isInactive ? "#9ca3af" : "#1e293b" }}>
                                {cc.cost_center_name}
                                {isInactive && (
                                  <span className="cc-inactive-badge">Inactive from {cc.deactivated_from_fy}</span>
                                )}
                              </span>
                            )}
                          </td>
                          {/* Business Unit */}
                          <td>
                            {draft.editing ? (
                              <select
                                className="admin-select"
                                style={{ width: "100%", fontSize: "12px" }}
                                value={draft.business_unit_id ?? ""}
                                onChange={e => updateCCField(origCC, "business_unit_id", e.target.value ? Number(e.target.value) : null)}
                              >
                                <option value="">— None —</option>
                                {buOptions.map(bu => <option key={bu.id} value={bu.id}>{bu.name}</option>)}
                              </select>
                            ) : (
                              <span style={{ fontSize: "13px", color: "#374151" }}>{cc.business_unit}</span>
                            )}
                          </td>
                          {/* Actions */}
                          <td>
                            <div className="admin-btn-row">
                              {draft.editing ? (
                                <>
                                  <button
                                    className="admin-btn admin-btn--primary"
                                    onClick={() => saveCC(origCC)}
                                    disabled={draft.saving}
                                  >
                                    <span className="material-icons">save</span>
                                    {draft.saving ? "…" : "Save"}
                                  </button>
                                  <button
                                    className="admin-btn admin-btn--ghost"
                                    onClick={() => cancelEditingCC(origCC)}
                                    disabled={draft.saving}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="admin-btn admin-btn--ghost"
                                    onClick={() => startEditingCC(origCC)}
                                  >
                                    <span className="material-icons">edit</span>
                                    Edit
                                  </button>
                                  <button
                                    className={`admin-btn ${isInactive ? "admin-btn--reactivate" : "admin-btn--deactivate"}`}
                                    onClick={() => toggleCCActive(origCC, isInactive)}
                                    title={isInactive ? "Reactivate cost center" : `Deactivate from ${currentFY}`}
                                  >
                                    <span className="material-icons">{isInactive ? "check_circle" : "block"}</span>
                                    {isInactive ? "Activate" : "Deactivate"}
                                  </button>
                                  <button
                                    className="admin-btn admin-btn--danger"
                                    onClick={() => deleteCC(origCC)}
                                    title="Delete cost center"
                                  >
                                    <span className="material-icons">delete</span>
                                  </button>
                                </>
                              )}
                            </div>
                            {draft.feedback && (
                              <div className={`admin-toast admin-toast--${draft.feedback.ok ? "ok" : "err"}`} style={{ marginTop: "4px" }}>
                                <span className="material-icons" style={{ fontSize: "13px" }}>
                                  {draft.feedback.ok ? "check_circle" : "error"}
                                </span>
                                {draft.feedback.msg}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredCCs.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
                          {search ? `No cost centers match "${search}"` : "No cost centers"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Accounts ────────────────────────────────────────────────── */}
      {activeTab === "accounts" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">category</span>
            <span className="admin-section-title">Account &amp; Category Manager</span>
            <div className="admin-kpi-row">
              <span className="admin-kpi-chip">
                <span className="material-icons">receipt_long</span>
                {accounts.length} Accounts
              </span>
            </div>
          </div>
          <div className="admin-section-body">

            {/* ── Toolbar ── */}
            <div className="admin-cc-toolbar">
              <div className="admin-search-wrap">
                <span className="material-icons">search</span>
                <input
                  className="admin-search"
                  type="text"
                  placeholder="Search account name or category…"
                  value={acctSearch}
                  onChange={e => setAcctSearch(e.target.value)}
                />
              </div>
              <button className="admin-btn admin-btn--ghost" onClick={loadAccounts}>
                <span className="material-icons">refresh</span>
                Refresh
              </button>
            </div>

            {/* ── Category datalist ── */}
            <datalist id="admin-categories">
              {allCategories.map(c => <option key={c} value={c} />)}
            </datalist>

            {acctLoading ? (
              <div style={{ color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="admin-cc-table">
                  <thead>
                    <tr>
                      <th style={{ width: "80px" }}>#</th>
                      <th>Account Name</th>
                      <th style={{ width: "200px" }}>Category</th>
                      <th style={{ width: "60px", textAlign: "center" }}>CCs</th>
                      <th style={{ width: "160px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acctsByCategory.flatMap(({ category, accounts: catAccts }) => [
                      <tr key={`hdr-${category}`} className="admin-acct-cat-hdr">
                        <td colSpan={5}>
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: CAT_COLOR(category), marginRight: 6, verticalAlign: "middle" }} />
                          {category}
                          <span style={{ marginLeft: 8, fontSize: "12px", color: "#94a3b8", fontWeight: 400 }}>{catAccts.length} accounts</span>
                        </td>
                      </tr>,
                      ...catAccts.map(acct => {
                        const draft = acctDrafts[acct.account];
                        if (!draft) return null;
                        const acctNum = acct.account.includes(" - ") ? acct.account.split(" - ")[0] : "";
                        return (
                          <tr key={acct.account}>
                            <td>
                              <span className="admin-cc-num" style={{ fontSize: "12px" }}>{acctNum}</span>
                            </td>
                            <td>
                              {draft.editing ? (
                                <input
                                  className="admin-text-input"
                                  style={{ width: "100%" }}
                                  value={draft.account}
                                  onChange={e => updateAcctField(acct.account, "account", e.target.value)}
                                />
                              ) : (
                                <span style={{ fontSize: "13px", color: "#1e293b" }}>
                                  {acct.account.includes(" - ") ? acct.account.split(" - ").slice(1).join(" - ") : acct.account}
                                </span>
                              )}
                            </td>
                            <td>
                              {draft.editing ? (
                                <input
                                  className="admin-text-input"
                                  list="admin-categories"
                                  value={draft.category}
                                  onChange={e => updateAcctField(acct.account, "category", e.target.value)}
                                />
                              ) : (
                                <span style={{ fontSize: "13px", color: "#374151" }}>{acct.category}</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span style={{ fontSize: "12px", color: "#9ca3af" }}>{acct.cc_count}</span>
                            </td>
                            <td>
                              <div className="admin-btn-row">
                                {draft.editing ? (
                                  <>
                                    <button
                                      className="admin-btn admin-btn--primary"
                                      onClick={() => saveAcct(acct.account)}
                                      disabled={draft.saving || !draft.account.trim() || !draft.category.trim()}
                                    >
                                      <span className="material-icons">save</span>
                                      {draft.saving ? "Saving…" : "Save"}
                                    </button>
                                    <button
                                      className="admin-btn admin-btn--ghost"
                                      onClick={() => cancelEditingAcct(acct.account)}
                                      disabled={draft.saving}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="admin-btn admin-btn--ghost"
                                      onClick={() => startEditingAcct(acct.account)}
                                    >
                                      <span className="material-icons">edit</span>
                                      Edit
                                    </button>
                                    <button
                                      className="admin-btn admin-btn--danger"
                                      onClick={() => deleteAcct(acct.account)}
                                      title="Delete account and all associated data"
                                    >
                                      <span className="material-icons">delete</span>
                                    </button>
                                  </>
                                )}
                              </div>
                              {draft.feedback && (
                                <div className={`admin-toast admin-toast--${draft.feedback.ok ? "ok" : "err"}`} style={{ marginTop: "4px" }}>
                                  <span className="material-icons" style={{ fontSize: "13px" }}>
                                    {draft.feedback.ok ? "check_circle" : "error"}
                                  </span>
                                  {draft.feedback.msg}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      }),
                    ])}
                    {filteredAccts.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
                          {acctSearch ? "No accounts match the current filter" : "No accounts loaded"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Unmapped Accounts Panel ── */}
            <div className="admin-unmapped-panel" style={{ marginTop: 24 }}>
              <button
                className="admin-unmapped-toggle"
                onClick={() => { setUnmappedOpen(o => !o); if (!unmappedOpen && unmappedAccts.length === 0) loadUnmapped(); }}
              >
                <span className="material-icons" style={{ fontSize: 18, transition: "transform .2s", transform: unmappedOpen ? "rotate(90deg)" : "none" }}>chevron_right</span>
                <span className="material-icons" style={{ fontSize: 18, color: "#f59e0b" }}>warning</span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>No FY Mapping</span>
                {unmappedAccts.length > 0 && (
                  <span className="admin-kpi-chip" style={{ marginLeft: 8, background: "#fef3c7", color: "#92400e" }}>
                    {unmappedAccts.length}
                  </span>
                )}
                <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 8 }}>
                  Accounts not assigned to any fiscal year
                </span>
              </button>

              {unmappedOpen && (
                <div style={{ marginTop: 8 }}>
                  {unmappedLoading ? (
                    <div style={{ color: "#9ca3af", fontSize: 13, padding: "12px 0" }}>Loading…</div>
                  ) : unmappedAccts.length === 0 ? (
                    <div style={{ color: "#22c55e", fontSize: 13, padding: "12px 0" }}>
                      <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>check_circle</span>
                      All accounts are mapped to at least one fiscal year
                    </div>
                  ) : (
                    <>
                      {/* Toolbar */}
                      <div className="admin-cc-toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                          <input type="checkbox" checked={unmappedSelected.size === unmappedAccts.length && unmappedAccts.length > 0} onChange={toggleAllUnmapped} />
                          Select all ({unmappedAccts.length})
                        </label>
                        {unmappedSelected.size > 0 && (
                          <span style={{ fontSize: 12, color: "#6366f1", fontWeight: 600 }}>{unmappedSelected.size} selected</span>
                        )}
                        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                          <button
                            className="admin-btn admin-btn--primary"
                            disabled={unmappedSelected.size === 0 || unmappedAssigning}
                            onClick={() => assignUnmappedToFY("FY25")}
                          >
                            <span className="material-icons">add_circle</span>
                            Assign FY25
                          </button>
                          <button
                            className="admin-btn admin-btn--primary"
                            disabled={unmappedSelected.size === 0 || unmappedAssigning}
                            onClick={() => assignUnmappedToFY("FY26")}
                          >
                            <span className="material-icons">add_circle</span>
                            Assign FY26
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            disabled={unmappedSelected.size === 0 || unmappedAssigning}
                            onClick={deleteUnmappedAccounts}
                          >
                            <span className="material-icons">delete</span>
                            Delete
                          </button>
                          <button className="admin-btn admin-btn--ghost" onClick={loadUnmapped}>
                            <span className="material-icons">refresh</span>
                          </button>
                        </div>
                      </div>

                      {/* Table */}
                      <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto", marginTop: 8 }}>
                        <table className="admin-cc-table">
                          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr>
                              <th style={{ width: 36 }}></th>
                              <th style={{ width: 80 }}>#</th>
                              <th>Account Name</th>
                              <th style={{ width: 160 }}>Category</th>
                              <th style={{ width: 90, textAlign: "center" }}>Budgets</th>
                              <th style={{ width: 90, textAlign: "center" }}>Actuals</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const grouped: Record<string, UnmappedAccount[]> = {};
                              unmappedAccts.forEach(a => { (grouped[a.category] = grouped[a.category] || []).push(a); });
                              return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).flatMap(([cat, accts]) => [
                                <tr key={`uhdr-${cat}`} className="admin-acct-cat-hdr">
                                  <td colSpan={6}>
                                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: CAT_COLOR(cat), marginRight: 6, verticalAlign: "middle" }} />
                                    {cat}
                                    <span style={{ marginLeft: 8, fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>{accts.length}</span>
                                  </td>
                                </tr>,
                                ...accts.map(a => {
                                  const acctNum = a.account.includes(" - ") ? a.account.split(" - ")[0] : "";
                                  const acctName = a.account.includes(" - ") ? a.account.split(" - ").slice(1).join(" - ") : a.account;
                                  return (
                                    <tr key={a.id} style={{ opacity: unmappedSelected.has(a.id) ? 1 : 0.75 }}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={unmappedSelected.has(a.id)}
                                          onChange={() => toggleUnmappedSelect(a.id)}
                                        />
                                      </td>
                                      <td><span className="admin-cc-num" style={{ fontSize: 12 }}>{acctNum}</span></td>
                                      <td><span style={{ fontSize: 13 }}>{acctName}</span></td>
                                      <td><span style={{ fontSize: 13, color: "#374151" }}>{a.category}</span></td>
                                      <td style={{ textAlign: "center" }}>
                                        <span style={{ fontSize: 12, color: a.budget_rows > 0 ? "#2563eb" : "#d1d5db" }}>
                                          {a.budget_rows}
                                        </span>
                                      </td>
                                      <td style={{ textAlign: "center" }}>
                                        <span style={{ fontSize: 12, color: a.actual_rows > 0 ? "#059669" : "#d1d5db" }}>
                                          {a.actual_rows}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                }),
                              ]);
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Actuals Dashboard ───────────────────────────────────────── */}
      {activeTab === "dashboard" && (
        <ActualsDashboard apiPrefix="/admin" fiscalYear={currentFY} ccsByBU={ccsByBU} />
      )}
      {/* ── Tab: Variance Report ──────────────────────────────────────────── */}
      {activeTab === "variancereport" && (
        <div className="admin-section vr-section">
          <div className="admin-section-header vr-no-print">
            <span className="material-icons">assessment</span>
            <span className="admin-section-title">Variance Report</span>
            <span className="admin-section-subtitle">Top 5 unfavorable &amp; favorable accounts per cost center</span>
            <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center" }}>
              {vrBusinessUnits.length > 0 && (
                <select className="admin-select" value={vrBU}
                  onChange={e => { const bu = e.target.value; setVrBU(bu); setVrAvailableMonths([]); setVrMonth(""); doLoadVRMonths(bu); }}>
                  <option value="">All Business Units</option>
                  {vrBusinessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                </select>
              )}
              {vrAvailableMonths.length > 0 && (
                <select className="admin-select" value={vrMonth}
                  onChange={e => { const m = e.target.value; setVrMonth(m); doLoadVRData(m, vrBU); }}>
                  {vrAvailableMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  <option value="YTD">YTD</option>
                </select>
              )}
              <button className="admin-btn" onClick={handleActionsReport}>
                <span className="material-icons">fact_check</span> Actions Report
              </button>
              <button className="admin-btn admin-btn--primary" onClick={handleVRPrint}>
                <span className="material-icons">picture_as_pdf</span> Export PDF
              </button>
            </div>
          </div>

          {vrMonthsLoading || vrLoading ? (
            <div style={{ padding: "32px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>Loading variance report…</div>
          ) : vrData.length === 0 ? (
            <div style={{ padding: "32px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>
              No variance data available. Upload actuals to generate this report.
            </div>
          ) : (() => {
            const isYTD = vrMonth === "YTD";
            const periodLabel = isYTD ? "YTD" : vrMonth;
            const VR_CATS = ["COMPENSATION", "OCCUPANCY", "OPERATING", "ADVERTISING", "ALLOCATED"] as const;
            const VR_CAT_COLORS: Record<string, string> = {
              COMPENSATION: "#4A90E2", OCCUPANCY: "#e84393", OPERATING: "#ea580c",
              ADVERTISING: "#7c3aed", ALLOCATED: "#475569",
            };

            // Build BU groups
            const buGroups: { bu: string; ccs: typeof vrData }[] = [];
            const buMap = new Map<string, typeof vrData>();
            vrData.forEach(cc => {
              const bu = cc.business_unit || "Other";
              if (!buMap.has(bu)) buMap.set(bu, []);
              buMap.get(bu)!.push(cc);
            });
            buMap.forEach((ccs, bu) => buGroups.push({ bu, ccs }));
            buGroups.sort((a, b) => a.bu.localeCompare(b.bu));

            // VP-level aggregates
            const totalBudget = vrData.reduce((s, c) => s + (isYTD ? c.ytd_budget : c.month_budget), 0);
            const totalActual = vrData.reduce((s, c) => s + (isYTD ? c.ytd_actual : c.month_actual), 0);
            const totalVariance = totalActual - totalBudget;
            const totalIsOver = totalVariance > 0;
            const totalUtilPct = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

            // Chart data: per-BU category breakdown (one chart per BU)
            const buCatCharts = buGroups.map(g => {
              const catData = VR_CATS.map(cat => {
                const budget = g.ccs.reduce((s, cc) => {
                  const cb = cc.category_breakdown?.find(c => c.category === cat);
                  return s + (cb ? (isYTD ? cb.ytd_budget : cb.month_budget) : 0);
                }, 0);
                const actual = g.ccs.reduce((s, cc) => {
                  const cb = cc.category_breakdown?.find(c => c.category === cat);
                  return s + (cb ? (isYTD ? cb.ytd_actual : cb.month_actual) : 0);
                }, 0);
                return { name: cat.charAt(0) + cat.slice(1).toLowerCase(), Budget: budget, Actual: actual };
              }).filter(d => d.Budget > 0 || d.Actual > 0);
              return { bu: g.bu, data: catData };
            });

            const fmtChartAxis = (v: number) => {
              if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
              if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1_000)}k`;
              return `$${v}`;
            };

            const renderBarLabel = (props: any) => {
              const { x, y, width, value } = props;
              if (!value) return null;
              const label = Math.abs(value) >= 1_000_000
                ? `$${(value / 1_000_000).toFixed(1)}M`
                : Math.abs(value) >= 1_000
                ? `$${Math.round(value / 1_000)}k`
                : `$${value}`;
              return (
                <text x={x + width / 2} y={y - 3} textAnchor="middle" fontSize={10} fill="#475569" fontWeight={600}>
                  {label}
                </text>
              );
            };

            return (
              <div className="vr-report">
                {/* ═══════════════════════════════════════════════════════════
                    PAGE 1: VP Summary
                    ═══════════════════════════════════════════════════════════ */}
                <div className="vr-page vr-vp-summary">
                  <div className="vr-print-header">
                    <div className="vr-print-title">Variance Report — {periodLabel} {currentFY}</div>
                    {vrBU && <div className="vr-print-subtitle">{vrBU}</div>}
                    <div className="vr-print-date">Generated {new Date().toLocaleDateString()}</div>
                  </div>

                  <div className="vr-page-title">
                    <span className="material-icons">summarize</span>
                    VP Summary — All Business Units
                  </div>

                  {/* Aggregate KPIs */}
                  <div className="dash-kpi-strip vr-vp-kpi-strip">
                    <div className="dash-kpi-card">
                      <div className="dash-kpi-label">
                        <span className="material-icons">account_balance</span>
                        {periodLabel} Budget
                      </div>
                      <div className="dash-kpi-value">{fmtMoney(totalBudget)}</div>
                      <div className="dash-kpi-sub">All business units combined</div>
                    </div>
                    <div className="dash-kpi-card">
                      <div className="dash-kpi-label">{periodLabel} Actual</div>
                      <div className="dash-kpi-value">{fmtMoney(totalActual)}</div>
                      <div className="dash-kpi-sub">Total organization spend</div>
                    </div>
                    <div className={`dash-kpi-card ${totalIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
                      <div className={`dash-kpi-label ${totalIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                        <span className="material-icons">{totalIsOver ? "warning" : "check_circle"}</span>
                        {totalIsOver ? "Over Budget" : "Under Budget"}
                      </div>
                      <div className={`dash-kpi-value ${totalIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                        {totalIsOver ? "+" : ""}{fmtMoney(Math.abs(totalVariance))}
                      </div>
                      <div className={`dash-kpi-sub ${totalIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                        {periodLabel} actual vs budget
                      </div>
                    </div>
                    <div className="dash-kpi-card">
                      <div className="dash-kpi-label">
                        <span className="material-icons">speed</span>
                        Utilization
                      </div>
                      <div className={`dash-kpi-value ${totalUtilPct > 100 ? "dash-kpi-value--red" : totalUtilPct > 90 ? "dash-kpi-value--amber" : "dash-kpi-value--green"}`}>
                        {Math.round(totalUtilPct)}%
                      </div>
                      <div className="dash-kpi-sub">Overall budget utilization</div>
                    </div>
                  </div>

                  {/* Category Spend Charts — one per BU */}
                  <div className="vr-bu-charts">
                    {buCatCharts.map(bc => (
                      <div key={bc.bu} className="vr-chart-block">
                        <div className="vr-chart-title">{bc.bu}</div>
                        {bc.data.length === 0 ? (
                          <div className="vr-empty">No category data</div>
                        ) : (
                          <div className="vr-chart-wrapper">
                            <ResponsiveContainer width="100%" height={160}>
                              <BarChart data={bc.data} barGap={1} barCategoryGap="20%"
                                margin={{ top: 16, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} axisLine={false} tickLine={false} interval={0} />
                                <YAxis tickFormatter={fmtChartAxis} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={48} />
                                <Tooltip formatter={(v: any) => fmtMoney(Number(v) || 0)} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                                <Bar dataKey="Actual" fill="#3b82f6" radius={[3, 3, 0, 0]} label={renderBarLabel} />
                                <Bar dataKey="Budget" fill="#8b5cf6" radius={[3, 3, 0, 0]} label={renderBarLabel} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ═══════════════════════════════════════════════════════════
                    PER-BU: BU Summary + CC Detail pages
                    ═══════════════════════════════════════════════════════════ */}
                {buGroups.map(group => {
                  const buBudget   = group.ccs.reduce((s, c) => s + (isYTD ? c.ytd_budget   : c.month_budget), 0);
                  const buActual   = group.ccs.reduce((s, c) => s + (isYTD ? c.ytd_actual   : c.month_actual), 0);
                  const buVariance = buActual - buBudget;
                  const buIsOver   = buVariance > 0;
                  const buUtilPct  = buBudget > 0 ? (buActual / buBudget) * 100 : 0;

                  return (
                    <React.Fragment key={group.bu}>
                      {/* ── BU Summary Page ── */}
                      <div className="vr-page vr-bu-summary">
                        <div className="vr-bu-header">
                          <span className="material-icons">business</span>
                          <span className="vr-bu-name">{group.bu}</span>
                          <span className="vr-bu-cc-count">{group.ccs.length} cost center{group.ccs.length !== 1 ? "s" : ""}</span>
                        </div>
                        <div className="dash-kpi-strip vr-bu-kpi-strip">
                          <div className="dash-kpi-card">
                            <div className="dash-kpi-label">
                              <span className="material-icons">account_balance</span>
                              {periodLabel} Budget
                            </div>
                            <div className="dash-kpi-value">{fmtMoney(buBudget)}</div>
                            <div className="dash-kpi-sub">Total {group.bu} budget</div>
                          </div>
                          <div className="dash-kpi-card">
                            <div className="dash-kpi-label">{periodLabel} Actual</div>
                            <div className="dash-kpi-value">{fmtMoney(buActual)}</div>
                            <div className="dash-kpi-sub">Total {group.bu} spend</div>
                          </div>
                          <div className={`dash-kpi-card ${buIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
                            <div className={`dash-kpi-label ${buIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                              <span className="material-icons">{buIsOver ? "warning" : "check_circle"}</span>
                              {buIsOver ? "Over Budget" : "Under Budget"}
                            </div>
                            <div className={`dash-kpi-value ${buIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                              {buIsOver ? "+" : ""}{fmtMoney(Math.abs(buVariance))}
                            </div>
                            <div className={`dash-kpi-sub ${buIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                              {periodLabel} actual vs budget
                            </div>
                          </div>
                          <div className="dash-kpi-card">
                            <div className="dash-kpi-label">
                              <span className="material-icons">speed</span>
                              Utilization
                            </div>
                            <div className={`dash-kpi-value ${buUtilPct > 100 ? "dash-kpi-value--red" : buUtilPct > 90 ? "dash-kpi-value--amber" : "dash-kpi-value--green"}`}>
                              {Math.round(buUtilPct)}%
                            </div>
                            <div className="dash-kpi-sub">BU budget utilization</div>
                          </div>
                        </div>

                        {/* CC Variance Table */}
                        <div className="vr-cc-table-label">Cost Center Variance — {group.bu}</div>
                        <table className="vr-table vr-cc-summary-table">
                          <thead>
                            <tr>
                              <th className="vr-th-left">Cost Center</th>
                              <th className="vr-th-left">Name</th>
                              <th className="vr-th-num">Budget</th>
                              <th className="vr-th-num">Actual</th>
                              <th className="vr-th-num">Variance</th>
                              <th className="vr-th-num">Util %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.ccs.map(cc => {
                              const b = isYTD ? cc.ytd_budget : cc.month_budget;
                              const a = isYTD ? cc.ytd_actual : cc.month_actual;
                              const v = a - b;
                              const u = b > 0 ? (a / b) * 100 : 0;
                              return (
                                <tr key={cc.cost_center}>
                                  <td className="vr-td-cc-num">{cc.cost_center}</td>
                                  <td className="vr-td-cc-name">{cc.cost_center_name}</td>
                                  <td className="vr-td-num">{fmtMoney(b)}</td>
                                  <td className="vr-td-num vr-td-num--bold">{fmtMoney(a)}</td>
                                  <td className={`vr-td-num ${v > 0 ? "dash-unfav" : v < 0 ? "dash-fav" : ""}`}>{fmtVar(v)}</td>
                                  <td className={`vr-td-num ${u > 100 ? "dash-unfav" : ""}`}>{Math.round(u)}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* ── CC Detail Pages (one per CC) ── */}
                      {group.ccs.map(cc => {
                        const budget   = isYTD ? cc.ytd_budget   : cc.month_budget;
                        const actual   = isYTD ? cc.ytd_actual   : cc.month_actual;
                        const variance = isYTD ? cc.ytd_variance  : cc.month_variance;
                        const isOver   = variance > 0;
                        const utilPct  = budget > 0 ? (actual / budget) * 100 : 0;
                        const ccActions = vrActionPlans[cc.cost_center] || [];
                        const openActions = ccActions.filter(a => a.status === "OPEN");
                        const closedActions = ccActions.filter(a => a.status === "CLOSED");

                        return (
                          <div key={cc.cost_center} className="vr-page vr-cc-detail">
                            {/* CC header */}
                            <div className="vr-cc-id">
                              <span className="vr-cc-number">{cc.cost_center}</span>
                              <span className="vr-cc-name">{cc.cost_center_name}</span>
                              <span className="vr-cc-bu">{cc.business_unit || "Other"}</span>
                            </div>

                            {/* KPI strip — compact horizontal bar */}
                            <div className="vr-cc-kpi-bar">
                              <div className="vr-cc-kpi-item">
                                <span className="vr-cc-kpi-label">{periodLabel} Budget</span>
                                <span className="vr-cc-kpi-value">{fmtMoney(budget)}</span>
                              </div>
                              <div className="vr-cc-kpi-item">
                                <span className="vr-cc-kpi-label">{periodLabel} Actual</span>
                                <span className="vr-cc-kpi-value">{fmtMoney(actual)}</span>
                              </div>
                              <div className={`vr-cc-kpi-item ${isOver ? "vr-cc-kpi-item--over" : "vr-cc-kpi-item--under"}`}>
                                <span className="vr-cc-kpi-label">{isOver ? "Over Budget" : "Under Budget"}</span>
                                <span className="vr-cc-kpi-value">{fmtVar(variance)}</span>
                              </div>
                              <div className={`vr-cc-kpi-item ${utilPct > 100 ? "vr-cc-kpi-item--over" : utilPct > 90 ? "vr-cc-kpi-item--warn" : ""}`}>
                                <span className="vr-cc-kpi-label">Utilization</span>
                                <span className="vr-cc-kpi-value">{Math.round(utilPct)}%</span>
                              </div>
                            </div>

                            {/* Over / Under tables — side by side */}
                            <div className="vr-tables-row">
                              <div className="vr-half">
                                <div className="vr-half-label vr-half-label--unfav">
                                  <span className="material-icons">trending_up</span> Over Budget ({cc.top_unfavorable.length})
                                </div>
                                {cc.top_unfavorable.length === 0 ? (
                                  <div className="vr-empty">All accounts within budget</div>
                                ) : (
                                  <table className="vr-table">
                                    <thead>
                                      <tr>
                                        <th className="vr-th-left">Account</th>
                                        <th className="vr-th-num">Budget</th>
                                        <th className="vr-th-num">Actual</th>
                                        <th className="vr-th-num">Variance</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cc.top_unfavorable.map(a => {
                                        const aBud = isYTD ? a.ytd_budget : a.month_budget;
                                        const aAct = isYTD ? a.ytd_actual : a.month_actual;
                                        const aVar = isYTD ? a.ytd_variance : a.month_variance;
                                        return (
                                          <tr key={a.account}>
                                            <td className="vr-td-acct">
                                              <span className="vr-acct-name">{a.account}</span>
                                              <span className="vr-acct-cat">{a.category}</span>
                                            </td>
                                            <td className="vr-td-num">{fmtMoney(aBud)}</td>
                                            <td className="vr-td-num">{fmtMoney(aAct)}</td>
                                            <td className="vr-td-num dash-unfav">{fmtVar(aVar)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>

                              <div className="vr-half">
                                <div className="vr-half-label vr-half-label--fav">
                                  <span className="material-icons">trending_down</span> Under Budget ({cc.top_favorable.length})
                                </div>
                                {cc.top_favorable.length === 0 ? (
                                  <div className="vr-empty">No accounts under budget</div>
                                ) : (
                                  <table className="vr-table">
                                    <thead>
                                      <tr>
                                        <th className="vr-th-left">Account</th>
                                        <th className="vr-th-num">Budget</th>
                                        <th className="vr-th-num">Actual</th>
                                        <th className="vr-th-num">Variance</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cc.top_favorable.map(a => {
                                        const aBud = isYTD ? a.ytd_budget : a.month_budget;
                                        const aAct = isYTD ? a.ytd_actual : a.month_actual;
                                        const aVar = isYTD ? a.ytd_variance : a.month_variance;
                                        return (
                                          <tr key={a.account}>
                                            <td className="vr-td-acct">
                                              <span className="vr-acct-name">{a.account}</span>
                                              <span className="vr-acct-cat">{a.category}</span>
                                            </td>
                                            <td className="vr-td-num">{fmtMoney(aBud)}</td>
                                            <td className="vr-td-num">{fmtMoney(aAct)}</td>
                                            <td className="vr-td-num dash-fav">{fmtVar(aVar)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </div>

                            {/* Action Plans */}
                            <div className="vr-action-plans">
                              <div className="vr-ap-header">
                                <span className="material-icons">fact_check</span>
                                Action Plan
                                <span className="vr-ap-counts">
                                  {openActions.length > 0 && <span className="vr-ap-count-open">{openActions.length} open</span>}
                                  {closedActions.length > 0 && <span className="vr-ap-count-closed">{closedActions.length} closed</span>}
                                </span>
                              </div>
                              {ccActions.length > 0 ? (
                                <div className="vr-ap-list">
                                  {ccActions.map((ap) => (
                                    <div key={ap.id} className={`vr-ap-item ${ap.status === "CLOSED" ? "vr-ap-item--closed" : ""}`}>
                                      <span className={`vr-ap-status vr-ap-status--${ap.status.toLowerCase()}`}>
                                        {ap.status}
                                      </span>
                                      <span className="vr-ap-text">{ap.action_text || "(no description)"}</span>
                                      {ap.category && <span className="vr-ap-cat">{ap.category}</span>}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="vr-ap-empty">No action items recorded</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}

          {/* Actions Report Modal */}
          {vrArOpen && (
            <div className="vr-ar-overlay" onClick={() => setVrArOpen(false)}>
              <div className="vr-ar-modal" onClick={e => e.stopPropagation()}>
                {/* Header with filter */}
                <div className="vr-ar-header">
                  <span className="vr-ar-title">Actions Report</span>
                  <span className="vr-ar-fy">{currentFY}{vrBU ? ` — ${vrBU}` : ""}</span>
                  <div className="vr-ar-header-right">
                    {vrArData && (
                      <div className="vr-ar-filters">
                        {(["ALL", "OPEN", "CLOSED"] as const).map(f => (
                          <button key={f} className={`vr-ar-pill ${vrArFilter === f ? "vr-ar-pill--active" : ""}`}
                            onClick={() => setVrArFilter(f)}>
                            {f === "ALL" ? "All" : f === "OPEN" ? "Open" : "Closed"}
                          </button>
                        ))}
                      </div>
                    )}
                    <button className="vr-ar-close" onClick={() => setVrArOpen(false)}>
                      <span className="material-icons">close</span>
                    </button>
                  </div>
                </div>

                {vrArLoading ? (
                  <div className="vr-ar-loading">Loading actions report...</div>
                ) : vrArData ? (() => {
                  const { items } = vrArData;
                  const filtered = vrArFilter === "ALL" ? items : items.filter(i => i.status === vrArFilter);

                  return (
                    <>
                      {/* Table body */}
                      <div className="vr-ar-body">
                        {filtered.length === 0 ? (
                          <div className="vr-ar-empty">No actions match the current filter</div>
                        ) : (
                          <table className="vr-ar-table">
                            <thead>
                              <tr>
                                <th>Month</th>
                                <th>CC</th>
                                <th>CC Name</th>
                                <th>Owner</th>
                                <th>Action</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map(item => (
                                <tr key={item.id} className={item.status === "CLOSED" ? "vr-ar-row--closed" : ""}>
                                  <td className="vr-ar-td-month">{item.month}</td>
                                  <td className="vr-ar-td-cc">{item.cost_center}</td>
                                  <td className="vr-ar-td-ccname">{item.cost_center_name}</td>
                                  <td className="vr-ar-td-owner">{item.cost_center_owner || "—"}</td>
                                  <td className="vr-ar-td-action">{item.action_text || "(no description)"}</td>
                                  <td className="vr-ar-td-status">
                                    <span className={`vr-ar-badge vr-ar-badge--${item.status.toLowerCase()}`}>{item.status}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="vr-ar-footer">
                        <span className="vr-ar-count">{filtered.length} action{filtered.length !== 1 ? "s" : ""}</span>
                        <button className="admin-btn" onClick={handleArCopy}>
                          <span className="material-icons">{vrArCopied ? "check" : "content_copy"}</span>
                          {vrArCopied ? "Copied!" : "Copy to Clipboard"}
                        </button>
                      </div>
                    </>
                  );
                })() : (
                  <div className="vr-ar-loading">No data available</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Forecast Comparison ─────────────────────────────────────── */}
      {activeTab === "buforecast" && (
        <div className="admin-section">
          {/* ── Comparison Selector ── */}
          <div className="fc-toolbar">
            <div className="fc-toolbar-selectors">
              <div className="fc-dim-group">
                <span className="fc-dim-group-label">Base</span>
                <select className="fc-dim-select" value={fcDimA}
                  onChange={e => { const d = e.target.value; setFcDimA(d); setFcSelectedCCs([]); doLoadForecast(d, fcDimB, fcBU, [], fcFYA, undefined); }}>
                  {ALL_DIMS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="fc-fy-select" value={fcFYA}
                  onChange={e => { const fy = e.target.value; setFcFYA(fy); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, fcBU, [], fy, undefined); }}>
                  {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                </select>
              </div>
              <span className="fc-vs-badge">vs</span>
              <div className="fc-dim-group">
                <span className="fc-dim-group-label">Compare</span>
                <select className="fc-dim-select" value={fcDimB}
                  onChange={e => { const d = e.target.value; setFcDimB(d); setFcSelectedCCs([]); doLoadForecast(fcDimA, d, fcBU, [], undefined, fcFYB); }}>
                  {ALL_DIMS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="fc-fy-select" value={fcFYB}
                  onChange={e => { const fy = e.target.value; setFcFYB(fy); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, fcBU, [], undefined, fy); }}>
                  {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                </select>
              </div>
            </div>
            <div className="fc-toolbar-actions">
              <select className="fc-bu-select" value={fcBU}
                onChange={e => { const bu = e.target.value; setFcBU(bu); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, bu, []); }}>
                <option value="">All Business Units</option>
                {fcBusinessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
              </select>
              <button className="fc-refresh-btn" onClick={() => doLoadForecast(fcDimA, fcDimB, fcBU, fcSelectedCCs)}>
                <span className="material-icons">refresh</span>
              </button>
            </div>
          </div>

          {/* ── CC filter pills (visible when a BU is selected) ── */}
          {fcBU && ccsByBU[fcBU] && ccsByBU[fcBU].length > 0 && (
            <div className="admin-cc-filter-bar">
              <span className="admin-cc-filter-label">Cost Centers:</span>
              <button
                className={`admin-cc-pill${fcSelectedCCs.length === 0 ? " admin-cc-pill--active" : ""}`}
                onClick={() => { setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, fcBU, []); }}
              >All</button>
              {ccsByBU[fcBU].map(cc => (
                <button
                  key={cc.cost_center_number}
                  className={`admin-cc-pill${fcSelectedCCs.includes(cc.cost_center_number) ? " admin-cc-pill--active" : ""}`}
                  title={cc.cost_center_name}
                  onClick={() => {
                    const next = fcSelectedCCs.includes(cc.cost_center_number)
                      ? fcSelectedCCs.filter(c => c !== cc.cost_center_number)
                      : [...fcSelectedCCs, cc.cost_center_number];
                    setFcSelectedCCs(next);
                    doLoadForecast(fcDimA, fcDimB, fcBU, next);
                  }}
                >{cc.cost_center_number}</button>
              ))}
            </div>
          )}

          {fcLoading ? (
            <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
          ) : fcData.length === 0 ? (
            <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
              No budget data found. Upload budgets for both dimensions to compare.
            </div>
          ) : (
            <>
              {/* ── KPI Summary Cards ── */}
              {(() => {
                const cats = fcData.filter(cat => !EXCLUDED_CATS.has(cat.category));
                const totalA = cats.reduce((s, c) => s + c.dim_a, 0);
                const totalB = cats.reduce((s, c) => s + c.dim_b, 0);
                const totalChg = totalB - totalA;
                const totalPct = totalA > 0 ? (totalChg / totalA) * 100 : 0;
                const dimALabel = buildDimName(fcDimA, fcFYA);
                const dimBLabel = buildDimName(fcDimB, fcFYB);
                return (
                  <div className="fc-summary-strip">
                    <div className="fc-summary-cell">
                      <span className="fc-summary-label">Base</span>
                      <span className="fc-summary-value">{fmtMoney(totalA)}</span>
                    </div>
                    <span className="fc-summary-arrow material-icons">arrow_forward</span>
                    <div className="fc-summary-cell">
                      <span className="fc-summary-label">Compare</span>
                      <span className="fc-summary-value">{fmtMoney(totalB)}</span>
                    </div>
                    <div className="fc-summary-divider" />
                    <div className={`fc-summary-cell ${totalChg > 0 ? "fc-summary-cell--unfav" : totalChg < 0 ? "fc-summary-cell--fav" : ""}`}>
                      <span className="fc-summary-label">Net Change</span>
                      <span className="fc-summary-value">{fmtVar(totalChg)}</span>
                    </div>
                    <div className={`fc-summary-cell ${totalChg > 0 ? "fc-summary-cell--unfav" : totalChg < 0 ? "fc-summary-cell--fav" : ""}`}>
                      <span className="fc-summary-label">Change %</span>
                      <span className="fc-summary-value">{totalA > 0 ? `${totalPct > 0 ? "+" : ""}${totalPct.toFixed(1)}%` : "—"}</span>
                    </div>
                  </div>
                );
              })()}

              {/* ── BU Summary table ── */}
              {!fcBU && fcBUSummary.length > 1 && (() => {
                const maxDim = Math.max(...fcBUSummary.map(r => Math.max(r.dim_a, r.dim_b)), 1);
                return (
                  <div className="admin-dash-bu-summary">
                    <table className="admin-dash-table">
                      <thead>
                        <tr>
                          <th>Business Unit</th>
                          <th className="num">{buildDimName(fcDimA, fcFYA)}</th>
                          <th className="num">{buildDimName(fcDimB, fcFYB)}</th>
                          <th className="num">Change</th>
                          <th className="num">Change %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fcBUSummary.map(row => {
                          const barA = Math.round((row.dim_a / maxDim) * 100);
                          const barB = Math.round((row.dim_b / maxDim) * 100);
                          return (
                            <tr key={row.business_unit} className="admin-dash-cc-row"
                              onClick={() => { setFcBU(row.business_unit); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, row.business_unit, []); }}
                              title={`Click to drill into ${row.business_unit}`}>
                              <td style={{ fontWeight: 600, fontSize: "13px" }}>
                                <div>{row.business_unit}</div>
                                <div className="fc-bu-inline-bars">
                                  <div className="fc-bu-bar fc-bu-bar--a" style={{ width: `${barA}%` }} />
                                  <div className="fc-bu-bar fc-bu-bar--b" style={{ width: `${barB}%` }} />
                                </div>
                              </td>
                              <td className="num" style={{ fontSize: "13px" }}>{fmtMoney(row.dim_a)}</td>
                              <td className="num" style={{ fontSize: "13px", fontWeight: 600 }}>{fmtMoney(row.dim_b)}</td>
                              <td className="num">
                                <span className={row.change > 0 ? "admin-dash-var-over" : row.change < 0 ? "admin-dash-var-under" : ""}>
                                  {fmtVar(row.change)}
                                </span>
                              </td>
                              <td className="num" style={{ fontSize: "13px", color: "#6b7280" }}>
                                {row.dim_a > 0 ? `${((row.change / row.dim_a) * 100).toFixed(1)}%` : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 700, borderTop: "2px solid #e5e7eb" }}>
                          <td style={{ fontSize: "13px" }}>Total</td>
                          <td className="num" style={{ fontSize: "13px" }}>{fmtMoney(fcBUSummary.reduce((s,r)=>s+r.dim_a,0))}</td>
                          <td className="num" style={{ fontSize: "13px" }}>{fmtMoney(fcBUSummary.reduce((s,r)=>s+r.dim_b,0))}</td>
                          <td className="num">{(() => { const v=fcBUSummary.reduce((s,r)=>s+r.change,0); return <span className={v>0?"admin-dash-var-over":v<0?"admin-dash-var-under":""}>{fmtVar(v)}</span>; })()}</td>
                          <td className="num" style={{ fontSize: "13px", color: "#6b7280" }}>
                            {(() => { const a=fcBUSummary.reduce((s,r)=>s+r.dim_a,0); const c=fcBUSummary.reduce((s,r)=>s+r.change,0); return a>0?`${((c/a)*100).toFixed(1)}%`:"—"; })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()}

              {/* ── Category cards (2×3 grid) ── */}
              <div className="fc-cat-grid">
                {fcData.filter(cat => !EXCLUDED_CATS.has(cat.category)).map(cat => {
                  const color   = CAT_COLOR(cat.category);
                  const chgPct  = cat.dim_a > 0 ? (cat.change / cat.dim_a) * 100 : 0;
                  const isActive = fcDrillCategory === cat.category;
                  return (
                    <div
                      key={cat.category}
                      className={`dash-cat-card${isActive ? " dash-cat-card--active" : ""}`}
                      style={{ borderTopColor: color, cursor: "pointer" }}
                      onClick={() => setFcDrillCategory(isActive ? null : cat.category)}
                      title={`Click to focus ${cat.category}`}
                    >
                      <div className="dash-cat-name" style={{ color }}>{cat.category}</div>
                      <div className="dash-cat-rows">
                        <div className="dash-cat-row">
                          <span className="dash-cat-row-label">{buildDimName(fcDimA, fcFYA)}</span>
                          <span className="dash-cat-row-val">{fmtMoney(cat.dim_a)}</span>
                        </div>
                        <div className="dash-cat-row">
                          <span className="dash-cat-row-label">{buildDimName(fcDimB, fcFYB)}</span>
                          <span className="dash-cat-row-val">{fmtMoney(cat.dim_b)}</span>
                        </div>
                        <div className={`dash-cat-row dash-cat-row--var ${cat.change <= 0 ? "dash-fav" : "dash-unfav"}`}>
                          <span className="dash-cat-row-label">Change</span>
                          <span className="dash-cat-row-val">{fmtMoney(Math.abs(cat.change))}</span>
                        </div>
                      </div>
                      <div className="dash-progress-wrap">
                        <div className="dash-progress-fill" style={{ transform: `scaleX(${Math.min(100, Math.abs(chgPct)) / 100})`, background: cat.change > 0 ? "#dc2626" : color }} />
                      </div>
                      <div className="dash-progress-label" style={{ color: cat.change > 0 ? "#dc2626" : cat.change < 0 ? "#15803d" : "#4b5563" }}>
                        {cat.dim_a > 0 ? `${Math.abs(chgPct).toFixed(1)}% change` : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Per-category sections ── */}
              {fcData.filter(cat => !EXCLUDED_CATS.has(cat.category) && (!fcDrillCategory || fcDrillCategory === cat.category)).map(cat => {
                const color     = CAT_COLOR(cat.category);
                const increased = cat.top_increased.filter(r => r.change > 0);
                const decreased = cat.top_decreased.filter(r => r.change < 0);
                const dimALabel = buildDimName(fcDimA, fcFYA);
                const dimBLabel = buildDimName(fcDimB, fcFYB);
                return (
                  <div key={cat.category} className="fc-category-frame" style={{ borderTopColor: color }}>
                    <div className="fc-category-header">
                      <span className="fc-category-swatch" style={{ background: color }} />
                      <span className="fc-category-name">{cat.category}</span>
                      <span className="fc-category-meta">
                        {fmtMoney(cat.dim_a)} → {fmtMoney(cat.dim_b)}
                        <span className="fc-category-meta-sep">·</span>
                        <span className={cat.change > 0 ? "dash-unfav" : cat.change < 0 ? "dash-fav" : ""}>{fmtVar(cat.change)}</span>
                      </span>
                    </div>
                    <div className="dash-tables-row">
                      {/* Increased */}
                      <div className="dash-table-card dash-table-card--unfav">
                        <div className="dash-table-head">
                          <span className="material-icons">trending_up</span>
                          Top 5 Increased ({dimBLabel})
                        </div>
                        {increased.length === 0 ? (
                          <div style={{ padding: "16px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>No cost centers increased</div>
                        ) : (
                          <table className="dash-tbl">
                            <thead><tr>
                              <th className="dash-th-left">CC</th>
                              <th className="dash-th-left">Name</th>
                              <th>{dimALabel}</th><th>{dimBLabel}</th><th>↑ Change</th>
                            </tr></thead>
                            <tbody>
                              {increased.map((r, i) => (
                                <tr key={r.cost_center} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                                  <td><span className="admin-cc-num" style={{ fontSize: "13px" }}>{r.cost_center}</span></td>
                                  <td className="dash-td-account">{r.cost_center_name}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_a)}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_b)}</td>
                                  <td className="dash-td-num dash-unfav">{fmtMoney(r.change)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                      {/* Decreased */}
                      <div className="dash-table-card dash-table-card--fav">
                        <div className="dash-table-head">
                          <span className="material-icons">trending_down</span>
                          Top 5 Decreased ({dimBLabel})
                        </div>
                        {decreased.length === 0 ? (
                          <div style={{ padding: "16px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>No cost centers decreased</div>
                        ) : (
                          <table className="dash-tbl">
                            <thead><tr>
                              <th className="dash-th-left">CC</th>
                              <th className="dash-th-left">Name</th>
                              <th>{dimALabel}</th><th>{dimBLabel}</th><th>↓ Change</th>
                            </tr></thead>
                            <tbody>
                              {decreased.map((r, i) => (
                                <tr key={r.cost_center} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                                  <td><span className="admin-cc-num" style={{ fontSize: "13px" }}>{r.cost_center}</span></td>
                                  <td className="dash-td-account">{r.cost_center_name}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_a)}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_b)}</td>
                                  <td className="dash-td-num dash-fav">{fmtMoney(Math.abs(r.change))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>

                    {/* ── Bar chart — cost center breakdown (vertical) ── */}
                    {(() => {
                      const allCCs = (cat.all_ccs || []);
                      if (allCCs.length === 0) return null;
                      const maxVal = Math.max(...allCCs.flatMap(r => [r.dim_a, r.dim_b]), 1);
                      const BAR_H = 130;
                      return (
                        <div className="fc-bar-chart">
                          <div className="fc-bar-chart-title">
                            <span className="material-icons">bar_chart</span>
                            Cost Center Breakdown
                            <span className="fc-bar-legend">
                              <span className="fc-bar-legend-swatch" style={{ background: color }} />{fcDimA}
                              <span className="fc-bar-legend-swatch" style={{ background: '#94a3b8' }} />{fcDimB}
                            </span>
                          </div>
                          <div className="fc-bar-cols">
                            {allCCs.map(r => {
                              const hA = Math.max(Math.round((r.dim_a / maxVal) * BAR_H), r.dim_a > 0 ? 2 : 0);
                              const hB = Math.max(Math.round((r.dim_b / maxVal) * BAR_H), r.dim_b > 0 ? 2 : 0);
                              return (
                                <div key={r.cost_center} className="fc-bar-col">
                                  <div className="fc-bar-col-bars" style={{ height: `${BAR_H}px` }}>
                                    <div className="fc-bar-v-wrap">
                                      <span className="fc-bar-v-val">{fmtMoney(r.dim_a)}</span>
                                      <div className="fc-bar-v" style={{ height: `${hA}px`, background: color }} />
                                    </div>
                                    <div className="fc-bar-v-wrap">
                                      <span className="fc-bar-v-val">{fmtMoney(r.dim_b)}</span>
                                      <div className="fc-bar-v" style={{ height: `${hB}px`, background: '#94a3b8' }} />
                                    </div>
                                  </div>
                                  <div className="fc-bar-col-label" title={r.cost_center_name || r.cost_center}>
                                    <span className="admin-cc-num" style={{ fontSize: "13px", display: "block" }}>{r.cost_center}</span>
                                    {r.cost_center_name || r.cost_center}
                                  </div>
                                  <div className={`fc-bar-col-change ${r.change > 0 ? 'dash-unfav' : r.change < 0 ? 'dash-fav' : ''}`}>
                                    {r.change !== 0 ? (r.change > 0 ? '+' : '') + fmtMoney(r.change) : '—'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ── Tab: Data Upload ─────────────────────────────────────────────── */}
      {activeTab === "upload" && (
        <div className="admin-upload-grid">

          {/* Actuals upload */}
          <div className="admin-section">
            <div className="admin-section-header">
              <span className="material-icons">receipt_long</span>
              <span className="admin-section-title">Upload Actuals</span>
              <span className="admin-section-subtitle">Corp BU · monthly actuals from finance report</span>
            </div>
            <div className="admin-section-body">
              <ActualsUploader fiscalYear={currentFY} onUploadSuccess={() => refreshInventory()} onBUChange={(bu) => { setActualsInvBU(bu); refreshInventory(bu); }} />
            </div>

            {/* ── Actuals inventory ── */}
            <div className="upload-inv-panel">
              <div className="upload-inv-title">
                <span className="material-icons">inventory_2</span>
                Actuals in Database
                {uploadInvLoad && <span className="material-icons upload-inv-spin">sync</span>}
              </div>
              {actualsInv.length === 0 && !uploadInvLoad ? (
                <p className="upload-inv-empty">No actuals uploaded yet.</p>
              ) : (() => {
                const fyList = Array.from(new Set<string>(actualsInv.map(r => r.fiscal_year).filter(Boolean))).sort().reverse();
                const activeFY = actualsInvFY || fyList[0] || "";
                const fyRows = actualsInv.filter(r => r.fiscal_year === activeFY);
                return (
                  <>
                    {/* FY pills */}
                    {fyList.length > 0 && (
                      <div className="upload-inv-fy-pills">
                        {fyList.map(fy => {
                          const count = actualsInv.filter(r => r.fiscal_year === fy).length;
                          return (
                            <button
                              key={fy}
                              className={`upload-inv-fy-pill${activeFY === fy ? " upload-inv-fy-pill--active" : ""}`}
                              onClick={() => setActualsInvFY(fy)}
                            >
                              {fy}
                              <span className="upload-inv-fy-pill-count">{count} mo</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* Month strip for selected FY */}
                    <div className="upload-inv-months">
                      {FISCAL_MONTHS.map(month => {
                        const row = fyRows.find(r => r.month === month);
                        const uploaded = !!row;
                        return (
                          <div key={month} className={`upload-inv-month ${uploaded ? "upload-inv-month--done" : "upload-inv-month--empty"}`}>
                            <div className="upload-inv-month-icon">
                              <span className="material-icons">{uploaded ? "check_circle" : "radio_button_unchecked"}</span>
                            </div>
                            <div className="upload-inv-month-name">{month.slice(0,3).toUpperCase()}</div>
                            {uploaded && (
                              <>
                                <div className="upload-inv-month-cc">{row.cc_count} CCs</div>
                                <div className="upload-inv-month-date">
                                  {new Date(row.last_upload).toLocaleDateString("en-US", { month:"short", day:"numeric" })}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Annual Budget upload */}
          <div className="admin-section">
            <div className="admin-section-header">
              <span className="material-icons">table_chart</span>
              <span className="admin-section-title">Upload Annual Budget</span>
              <span className="admin-section-subtitle">Per cost center · annual budget template</span>
            </div>
            <div className="admin-section-body">
              <BudgetUploader fiscalYear={currentFY} onUploadSuccess={() => refreshInventory()} />
            </div>

            {/* ── Budget inventory ── */}
            <div className="upload-inv-panel">
              <div className="upload-inv-title">
                <span className="material-icons">inventory_2</span>
                Budget in Database
                {uploadInvLoad && <span className="material-icons upload-inv-spin">sync</span>}
              </div>
              {budgetInv.length === 0 && !uploadInvLoad ? (
                <p className="upload-inv-empty">No budgets uploaded yet.</p>
              ) : (
                <>
                  {/* FY pills — only show dimensions with a FY suffix, strip "Annual Budget " prefix */}
                  <div className="upload-inv-fy-pills">
                    {budgetInv.filter(d => /FY\d+/i.test(d.dimension)).map(d => {
                      const fyLabel = d.dimension.replace(/^Annual Budget\s*/i, "");
                      return (
                        <button
                          key={d.dimension}
                          className={`upload-inv-fy-pill${uploadInvFY === d.dimension ? " upload-inv-fy-pill--active" : ""}`}
                          onClick={() => setUploadInvFY(d.dimension)}
                        >
                          {fyLabel}
                          <span className="upload-inv-fy-pill-count">
                            {d.business_units.reduce((s, b) => s + b.ccs.length, 0)} CCs
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* BU sections */}
                  {(budgetInv.find(d => d.dimension === uploadInvFY)?.business_units ?? []).map(bu => (
                    <div key={bu.business_unit} className="upload-inv-bu">
                      <div className="upload-inv-bu-header">
                        <span className="material-icons" style={{ fontSize: 14, color: "#6b7280" }}>business</span>
                        <span className="upload-inv-bu-name">{bu.business_unit}</span>
                        <span className="upload-inv-bu-meta">{bu.ccs.length} cost centers</span>
                        <span className="upload-inv-bu-total">{fmtMoney(bu.total)}</span>
                      </div>
                      <div className="upload-inv-cc-chips">
                        {bu.ccs.map(cc => (
                          <div key={cc.cost_center_number} className="upload-inv-cc-chip" title={cc.cost_center_name}>
                            <span className="upload-inv-cc-num">{cc.cost_center_number}</span>
                            <span className="upload-inv-cc-name">{cc.cost_center_name}</span>
                            <span className="upload-inv-cc-budget">{fmtMoney(cc.total_budget)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

        </div>
      )}

      {/* ── Tab: Salary Config ───────────────────────────────────────────── */}
      {activeTab === "salary" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">payments</span>
            <span className="admin-section-title">Salary & BIP Configuration</span>
            <span className="admin-section-subtitle">Raise percentages & rate tables per fiscal year</span>
            <div className="sal-fy-picker">
              <span className="sal-fy-label">Fiscal Year</span>
              <select
                className="admin-select"
                value={salaryCfgFY}
                onChange={e => { const fy = e.target.value; setSalaryCfgFY(fy); loadSalaryCfg(fy); }}
              >
                {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
              </select>
            </div>
          </div>
          <div className="admin-section-body">
            {salaryCfgLoading ? (
              <p className="sal-loading">Loading…</p>
            ) : (
              <>
                {/* ── Raise % inputs ── */}
                <div className="sal-card">
                  <div className="sal-card-title">Raise % Settings</div>
                  <div className="sal-field-list">
                    <div className="sal-field-row">
                      <label className="sal-field-label" title="Applied to salary calculations for April and May">
                        Raise % before Month 3 <span className="sal-field-hint">(Apr / May)</span>
                      </label>
                      <div className="sal-field-input">
                        <input
                          className="sal-pct-input"
                          type="number" min={0} max={100} step={0.01}
                          value={salaryRaiseBefore}
                          onChange={e => { setSalaryRaiseBefore(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-pct-sign">%</span>
                      </div>
                    </div>
                    <div className="sal-field-row">
                      <label className="sal-field-label" title="Applied to salary calculations from June through March">
                        Raise % from Month 3 onward <span className="sal-field-hint">(Jun – Mar)</span>
                      </label>
                      <div className="sal-field-input">
                        <input
                          className="sal-pct-input"
                          type="number" min={0} max={100} step={0.01}
                          value={salaryRaiseAfter}
                          onChange={e => { setSalaryRaiseAfter(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-pct-sign">%</span>
                      </div>
                    </div>
                    <div className="sal-field-row">
                      <label className="sal-field-label" title="Bonus Incentive Plan raise applied uniformly across all 12 months">
                        BIP Raise % <span className="sal-field-hint">(flat · all 12 months)</span>
                      </label>
                      <div className="sal-field-input">
                        <input
                          className="sal-pct-input"
                          type="number" min={0} max={100} step={0.01}
                          value={salaryBipRaise}
                          onChange={e => { setSalaryBipRaise(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-pct-sign">%</span>
                      </div>
                    </div>
                  </div>
                  <div className="sal-save-row">
                    <button className="admin-fy-btn" onClick={saveSalaryCfg}>Save</button>
                    {salaryCfgFeedback && (
                      <span className={`sal-feedback ${salaryCfgFeedback.ok ? "sal-feedback--ok" : "sal-feedback--err"}`}>
                        {salaryCfgFeedback.msg}
                      </span>
                    )}
                  </div>
                </div>

                {/* ── Rate Reference Table (editable) ── */}
                <div className="sal-rates-panel">
                  <div className="sal-card-title">Rate Reference Table</div>

                  {/* 60120 Payroll Tax — all 12 months in one row */}
                  <div className="sal-rate-section">
                    <div className="sal-rate-label" title="Percentage of (Salary + BIP) withheld for payroll taxes each month">
                      60120 – Payroll Tax (% of Salary + BIP)
                    </div>
                    <div className="sal-month-grid">
                      {PAYROLL_TAX_MONTHS.map(({ key, label }) => (
                        <div key={key} className="sal-month-cell" title={`Payroll tax rate for ${label}`}>
                          <div className="sal-month-label">{label}</div>
                          <div className="sal-month-input-wrap">
                            <input
                              className="sal-month-input"
                              type="number" min={0} max={100} step={0.01}
                              value={payrollTax[key] ?? ""}
                              onChange={e => { setPayrollTax(p => ({ ...p, [key]: e.target.value })); setSalaryCfgFeedback(null); }}
                            />
                            <span className="sal-month-sign">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 60100 Insurance + 60130 Retirement */}
                  <div className="sal-benefits-row">
                    <div className="sal-benefit-card" title="Monthly cost per full-time employee for health and insurance benefits">
                      <div className="sal-rate-label">60100 – Insurance Benefits</div>
                      <div className="sal-benefit-input-wrap">
                        <span className="sal-benefit-prefix">$</span>
                        <input
                          className="sal-benefit-input"
                          type="number" min={0} step={0.01}
                          value={insurancePerHead}
                          onChange={e => { setInsurancePerHead(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-benefit-suffix">/ FT head / month</span>
                      </div>
                    </div>
                    <div className="sal-benefit-card" title="Retirement plan contribution as a percentage of Salary + BIP, applied to all 12 months">
                      <div className="sal-rate-label">60130 – Retirement Plans</div>
                      <div className="sal-benefit-input-wrap">
                        <input
                          className="sal-benefit-input"
                          type="number" min={0} max={100} step={0.0001}
                          value={retirementRate}
                          onChange={e => { setRetirementRate(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-benefit-suffix">% of (Salary + BIP) · all 12 months</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Finance View ────────────────────────────────────────────── */}
      {activeTab === "finance" && (
        <div className="admin-section">
          {financeLoading ? (
            <div className="fv-loading">
              <span className="material-icons fv-spin" style={{ fontSize: 20, marginBottom: 8 }}>sync</span>
              <div>Loading forecast data...</div>
            </div>
          ) : (() => {
            const visRows = financeRows.filter(r => !financeFilterBU || r.business_unit === financeFilterBU);
            const sample  = visRows[0];
            const abLabel  = sample?.annual_budget_dim  ?? "Annual Budget";
            const refLabel = sample?.ref_dimension      ?? "October Forecast";
            const curLabel = sample?.dimension          ?? "Forecast Total";
            const showRef  = refLabel !== abLabel;
            const showCur  = curLabel !== abLabel;  // hide duplicate when current dim IS Annual Budget
            // Dynamic previous/current labels & totals
            const prevLabel = showRef ? refLabel : abLabel;
            const totalAB       = visRows.reduce((s, r) => s + (r.annual_budget_total ?? 0), 0);
            const totalRef      = visRows.reduce((s, r) => s + (r.ref_forecast_total ?? 0), 0);
            const totalForecast = visRows.reduce((s, r) => s + (r.dimension_total ?? 0), 0);
            const totalPrev     = showRef ? totalRef : totalAB;
            const metCount  = visRows.filter(r => r.met).length;
            const overCount = visRows.length - metCount;
            const buList = Array.from(new Set(financeRows.map(r => r.business_unit).filter(Boolean))).sort();

            return (
              <>
                {/* KPI Summary */}
                <div className="fv-kpi-row">
                  <div className="fv-kpi-card">
                    <span className="fv-kpi-label">{prevLabel}</span>
                    <span className="fv-kpi-value">${(totalPrev / 1_000_000).toFixed(1)}M</span>
                  </div>
                  {showCur && (
                    <div className="fv-kpi-card">
                      <span className="fv-kpi-label">{curLabel}</span>
                      <span className="fv-kpi-value">${(totalForecast / 1_000_000).toFixed(1)}M</span>
                    </div>
                  )}
                  <div className="fv-kpi-card fv-kpi-card--met">
                    <span className="fv-kpi-label">On Target</span>
                    <span className="fv-kpi-value">{metCount}</span>
                  </div>
                  <div className="fv-kpi-card fv-kpi-card--over">
                    <span className="fv-kpi-label">Over Target</span>
                    <span className="fv-kpi-value">{overCount}</span>
                  </div>
                </div>

                {/* Toolbar: BU filter + refresh */}
                <div className="fv-toolbar">
                  <div className="fv-toolbar-left">
                    {buList.length > 1 && (
                      <select
                        className="fv-bu-select"
                        value={financeFilterBU}
                        onChange={e => setFinanceFilterBU(e.target.value)}
                      >
                        <option value="">All Business Units</option>
                        {buList.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                      </select>
                    )}
                    {sample?.dimension && (
                      <span className="fv-dimension-badge">{sample.dimension}</span>
                    )}
                  </div>
                  <div className="fv-toolbar-right">
                    <button className="admin-btn admin-btn--ghost" onClick={loadFinanceTargets}>
                      <span className="material-icons" style={{ fontSize: 16 }}>refresh</span>
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Main Table */}
                <div style={{ overflowX: "auto" }}>
                  <table className="fv-table">
                    <thead>
                      <tr>
                        <th>Cost Center</th>
                        <th className="fv-th-right">{prevLabel}</th>
                        {showCur && <th className="fv-th-right">{curLabel}</th>}
                        {showCur && <th className="fv-th-right">Variance</th>}
                        <th className="fv-th-right">Variance Allowed</th>
                        <th className="fv-th-right">Target</th>
                        <th className="fv-th-center">Status</th>
                        <th className="fv-th-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visRows.map(row => {
                        const cc       = row.cost_center_number;
                        const abTotal  = row.annual_budget_total    ?? 0;
                        const refTotal = row.ref_forecast_total     ?? 0;
                        const dimTotal = row.dimension_total        ?? 0;
                        const prevTotal = showRef ? refTotal : abTotal;
                        const variance  = prevTotal - dimTotal;
                        const janAV    = row.january_av             ?? 0;
                        const target   = prevTotal + janAV;
                        const isMet    = dimTotal <= target;
                        const pct      = target > 0 ? Math.min((dimTotal / target) * 100, 150) : 0;
                        const isEditingJanAV = editingJanAV[cc] !== undefined;
                        const avDraft  = editingJanAV[cc] ?? String(janAV);
                        const isSaving = savingTarget === cc;
                        const isDling  = downloadingCC === cc;
                        return (
                          <tr key={cc}>
                            {/* Cost Center: CC#, name, owner, BU combined */}
                            <td>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span className="fv-cc-num">{cc}</span>
                                  <span className="fv-cc-name">{row.cost_center_name}</span>
                                </div>
                                <div className="fv-cc-meta">
                                  {row.cost_center_owner}{row.business_unit ? ` · ${row.business_unit}` : ""}
                                </div>
                              </div>
                            </td>
                            {/* Previous Dimension (AB or Ref Forecast) */}
                            <td className="fv-amount fv-amount--dim">
                              ${Math.round(prevTotal).toLocaleString()}
                            </td>
                            {/* Current Forecast — hidden when same as AB */}
                            {showCur && (
                              <td className="fv-amount fv-amount--primary">
                                ${Math.round(dimTotal).toLocaleString()}
                              </td>
                            )}
                            {/* Variance = Previous - Current */}
                            {showCur && (
                              <td className={`fv-amount ${variance > 0 ? "fv-amount--red" : variance < 0 ? "fv-amount--green" : ""}`}>
                                {variance > 0 ? `+$${Math.round(variance).toLocaleString()}` : variance < 0 ? `-$${Math.round(Math.abs(variance)).toLocaleString()}` : "$0"}
                              </td>
                            )}
                            {/* Variance Allowed — editable */}
                            <td className="fv-amount">
                              {isEditingJanAV ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                  <input
                                    autoFocus
                                    className="fv-target-input"
                                    type="number" step={1000} min={0}
                                    value={avDraft}
                                    onChange={e => setEditingJanAV(prev => ({ ...prev, [cc]: e.target.value }))}
                                    onBlur={() => saveJanAV(cc)}
                                    onKeyDown={e => {
                                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                      if (e.key === "Escape") setEditingJanAV(e2 => { const n = { ...e2 }; delete n[cc]; return n; });
                                    }}
                                  />
                                  {isSaving && <span className="material-icons fv-spin" style={{ fontSize: 14, color: "#6b7280" }}>sync</span>}
                                </div>
                              ) : (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                  <span className={janAV === 0 ? "fv-amount--dim" : "fv-amount--green"}>
                                    {janAV === 0 ? "$0" : `+$${Math.round(janAV).toLocaleString()}`}
                                  </span>
                                  <button
                                    className="fv-edit-btn"
                                    title="Edit allowed variance"
                                    onClick={() => setEditingJanAV(prev => ({ ...prev, [cc]: String(janAV) }))}
                                  >
                                    <span className="material-icons" style={{ fontSize: 14 }}>edit</span>
                                  </button>
                                </div>
                              )}
                            </td>
                            {/* Target */}
                            <td className="fv-amount fv-amount--bold">
                              ${Math.round(target).toLocaleString()}
                            </td>
                            {/* Status */}
                            <td style={{ textAlign: "center" }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                {isMet
                                  ? <span className="fv-chip fv-chip--met"><span className="material-icons" style={{ fontSize: 12 }}>check_circle</span> Met</span>
                                  : <span className="fv-chip fv-chip--over"><span className="material-icons" style={{ fontSize: 12 }}>warning</span> Over</span>
                                }
                                <div className="fv-progress-wrap">
                                  <div
                                    className={`fv-progress-bar ${isMet ? "fv-progress-bar--met" : "fv-progress-bar--over"}`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            {/* Download */}
                            <td style={{ textAlign: "center" }}>
                              <button
                                className="fv-dl-btn"
                                onClick={() => handleFinanceDownload(cc)}
                                disabled={isDling}
                                title={`Download ${cc} plan`}
                              >
                                <span className="material-icons" style={{ fontSize: 16 }}>
                                  {isDling ? "hourglass_empty" : "download"}
                                </span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Variance Allowances sub-section */}
                {varRows.length > 0 && (
                  <div className="fv-variance-section">
                    <div className="fv-variance-header">
                      <span className="fv-variance-title">Forecast Variance Allowances</span>
                      <span className="fv-variance-desc">Max $ a forecast may exceed the Annual Budget per cost center</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table className="fv-table">
                        <thead>
                          <tr>
                            <th>Cost Center</th>
                            {["July Forecast", "October Forecast", "January Forecast"].map(dim => (
                              <th key={dim} className="fv-th-right" style={{ color: dim === varActiveDim ? "#2563eb" : undefined }}>
                                {dim.replace(" Forecast", "")} Variance
                                {dim === varActiveDim && (
                                  <span className="fv-dimension-badge" style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px" }}>Active</span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {varRows.filter(r => !financeFilterBU || r.business_unit === financeFilterBU).map(row => (
                            <tr key={row.cost_center_number}>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span className="fv-cc-num">{row.cost_center_number}</span>
                                  <span className="fv-cc-name">{row.cost_center_name}</span>
                                </div>
                              </td>
                              {["July Forecast", "October Forecast", "January Forecast"].map(dim => {
                                const val = row.dims[dim] ?? 0;
                                const isActive = dim === varActiveDim;
                                return (
                                  <td key={dim} className="fv-amount" style={{ color: val === 0 ? "#9ca3af" : (isActive ? "#374151" : "#9ca3af"), background: !isActive ? "#fafbfc" : undefined }}>
                                    {val === 0 ? "$0" : `$${val.toLocaleString()}`}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ── Export Template Modal ────────────────────────────────────────── */}
      {exportModalCC && (
        <div className="export-modal-overlay" onClick={() => !exporting && setExportModalCC(null)}>
          <div className="export-modal" onClick={e => e.stopPropagation()}>
            <div className="export-modal-header">
              <span className="material-icons">file_download</span>
              <span>Export &mdash; CC {exportModalCC}</span>
              <button className="export-modal-close" onClick={() => setExportModalCC(null)} disabled={exporting}>
                <span className="material-icons">close</span>
              </button>
            </div>

            {/* Step 1: File Upload */}
            {!exportValidation && !exportNeedsPassword && (
              <div className="export-modal-body">
                <p className="export-modal-desc">
                  Upload the budget file for CC {exportModalCC}. The system will write the current forecast data into the Plan, Salary, and BIP tabs, preserving your formulas and automations.
                </p>
                <label
                  className={`export-drop-zone ${exportValidating ? "export-drop-zone--loading" : ""}`}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("export-drop-zone--hover"); }}
                  onDragLeave={e => e.currentTarget.classList.remove("export-drop-zone--hover")}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("export-drop-zone--hover"); const f = e.dataTransfer.files[0]; if (f) handleExportValidate(f); }}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleExportValidate(f); }}
                  />
                  <span className="material-icons export-drop-icon">upload_file</span>
                  <span className="export-drop-text">
                    {exportValidating ? "Validating..." : "Drop budget file here or click to browse"}
                  </span>
                  <span className="export-drop-hint">.xlsx budget template for CC {exportModalCC}</span>
                </label>
                {exportError && <div className="export-error">{exportError}</div>}
              </div>
            )}

            {/* Step 1b: Password Required */}
            {!exportValidation && exportNeedsPassword && (
              <div className="export-modal-body">
                <p className="export-modal-desc">
                  This file is password-protected. Enter the password to continue.
                </p>
                <div className="export-pw-row">
                  <span className="material-icons export-pw-icon">lock</span>
                  <input
                    type="password"
                    className="export-pw-input"
                    placeholder="File password"
                    value={exportPassword}
                    onChange={e => setExportPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleExportPasswordSubmit(); }}
                    autoFocus
                  />
                </div>
                {exportFile && (
                  <div className="export-pw-file">
                    <span className="material-icons" style={{ fontSize: 14 }}>description</span>
                    {exportFile.name}
                  </div>
                )}
                {exportError && <div className="export-error">{exportError}</div>}
                <div className="export-modal-actions">
                  <button
                    className="export-btn-secondary"
                    onClick={() => { setExportNeedsPassword(false); setExportFile(null); setExportPassword(""); setExportError(null); }}
                  >
                    Change File
                  </button>
                  <button
                    className="export-btn-primary"
                    onClick={handleExportPasswordSubmit}
                    disabled={!exportPassword || exportValidating}
                  >
                    {exportValidating ? "Unlocking..." : "Unlock & Continue"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Verification */}
            {exportValidation && (
              <div className="export-modal-body">
                {!exportValidation.ccMatch && (
                  <div className="export-error" style={{ marginBottom: 14 }}>
                    <strong>Cost center mismatch:</strong> This file belongs to CC {exportValidation.fileCC}, but you are exporting CC {exportModalCC}. Please upload the correct file.
                  </div>
                )}
                <p className="export-modal-desc">
                  Select the dimension to export, verify the tabs below, then click Export.
                </p>

                <div className="export-dim-selector">
                  {["Annual Budget", "July Forecast", "October Forecast", "January Forecast"].map(dim => (
                    <button
                      key={dim}
                      className={`export-dim-btn ${exportDimension === dim ? "export-dim-btn--active" : ""}`}
                      onClick={() => setExportDimension(dim)}
                      disabled={exporting}
                    >
                      {dim}
                    </button>
                  ))}
                </div>

                <div className="export-verify-heading">Sheets to write:</div>
                <div className="export-verify-grid">
                  <div className={`export-verify-card ${exportValidation.sheets.salary ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportValidation.sheets.salary ? "check_circle" : "cancel"}</span>
                    <span className="export-verify-label">Salary60010</span>
                    <span className="export-verify-detail">{exportValidation.sheets.salary ? `${exportValidation.counts.employees} employees` : "Not found"}</span>
                  </div>
                  <div className={`export-verify-card ${exportValidation.sheets.bip ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportValidation.sheets.bip ? "check_circle" : "cancel"}</span>
                    <span className="export-verify-label">BIP60022</span>
                    <span className="export-verify-detail">{exportValidation.sheets.bip ? `${exportValidation.counts.employees} employees` : "Not found"}</span>
                  </div>
                  <div className={`export-verify-card ${exportPlanSheet ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportPlanSheet ? "check_circle" : "cancel"}</span>
                    {exportValidation.sheets.planSheets && exportValidation.sheets.planSheets.length > 1 ? (
                      <select
                        className="export-plan-select"
                        value={exportPlanSheet}
                        onChange={e => setExportPlanSheet(e.target.value)}
                      >
                        {exportValidation.sheets.planSheets.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="export-verify-label">{exportPlanSheet || "PLANFY__ (missing)"}</span>
                    )}
                    <span className="export-verify-detail">{exportPlanSheet ? `${exportValidation.counts.budgetAccounts} accounts` : "No plan sheet found"}</span>
                  </div>
                </div>

                <div className="export-meta">
                  <div><strong>Dimension:</strong> {exportDimension} {exportValidation.fy}</div>
                  <div><strong>Output file:</strong> {exportFile?.name?.replace(/\.[^.]+$/, "")}_{({"Annual Budget":"ab","July Forecast":"jff","October Forecast":"off","January Forecast":"janff"} as Record<string,string>)[exportDimension] || "ab"}_{new Date().toISOString().slice(0,10).replace(/-/g,"")}.xlsx</div>
                  {exportFile && <div><strong>Template:</strong> {exportFile.name}</div>}
                </div>

                {!exportValidation.valid && (
                  <div className="export-error">
                    Template is missing expected sheets. Found: {exportValidation.allSheets.join(", ")}
                  </div>
                )}
                {exportError && <div className="export-error">{exportError}</div>}

                <div className="export-modal-actions">
                  <button
                    className="export-btn-secondary"
                    onClick={() => { setExportValidation(null); setExportFile(null); setExportPassword(""); setExportNeedsPassword(false); setExportError(null); }}
                    disabled={exporting}
                  >
                    Change File
                  </button>
                  <button
                    className="export-btn-primary"
                    onClick={handleExportDownload}
                    disabled={exporting || !exportValidation.valid || !exportValidation.ccMatch}
                  >
                    {exporting ? "Exporting..." : "Export & Download"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Temp password confirmation modal ─────────────────────────────── */}
      {tempPwModal && (
        <div className="dl-summary-overlay" onClick={() => !tempPwSetting && setTempPwModal(null)}>
          <div className="dl-summary-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
              <span className="material-icons" style={{ color: "#2563eb", fontSize: 22, marginTop: 2 }}>key</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Reset Temporary Password</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>
                  You are about to reset the password for <strong>{tempPwModal.owner}</strong> (CC {tempPwModal.cc}).
                  A temporary password will be generated and the following email message will be copied to your clipboard.
                </div>
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              Email preview
            </div>
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "12px 14px", fontSize: 13, color: "#1e293b", lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 16, fontFamily: "inherit" }}>
              <span style={{ color: "#64748b" }}>Subject: </span>Temp QFT Password{"\n\n"}
              Hello {tempPwModal.owner},{"\n\n"}
              Your temporary QFT password is: <strong style={{ fontFamily: "monospace", letterSpacing: "0.05em" }}>{tempPwModal.password}</strong>{"\n\n"}
              You will be prompted to create a new password the next time you log in. Please keep this password secure.{"\n\n"}
              Best regards,{"\n"}QFT Admin
            </div>

            {tempPwModalErr && (
              <div className="admin-toast admin-toast--err" style={{ marginBottom: 12 }}>
                <span className="material-icons" style={{ fontSize: 13 }}>error</span>
                {tempPwModalErr}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="admin-btn admin-btn--ghost" onClick={() => setTempPwModal(null)} disabled={tempPwSetting}>
                Cancel
              </button>
              <button className="admin-btn admin-btn--primary" onClick={doSetTempPassword} disabled={tempPwSetting}>
                <span className="material-icons" style={{ fontSize: 16 }}>
                  {tempPwSetting ? "hourglass_empty" : "content_copy"}
                </span>
                {tempPwSetting ? "Setting…" : "Set Password & Copy Message"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Temp password success toast ───────────────────────────────────── */}
      {tempPwDoneMsg && (
        <div className="admin-selection-toast" style={{ background: "#f0fdf4", borderColor: "#86efac", color: "#15803d" }}>
          <span className="material-icons" style={{ color: "#16a34a" }}>mark_email_read</span>
          {tempPwDoneMsg}
          <button className="admin-selection-toast-close" onClick={() => setTempPwDoneMsg(null)}>
            <span className="material-icons">close</span>
          </button>
        </div>
      )}

      {/* ── Selection toast ───────────────────────────────────────────────── */}
      {selectionMsg && (
        <div className="admin-selection-toast">
          <span className="material-icons">check_circle</span>
          {selectionMsg}
          <button className="admin-selection-toast-close" onClick={() => setSelectionMsg(null)}>
            <span className="material-icons">close</span>
          </button>
        </div>
      )}

      {/* ── Delete confirmation modal ──────────────────────────────────────── */}
      {deleteConfirm && (
        <div className="dl-summary-overlay" onClick={() => !deletePwBusy && setDeleteConfirm(null)}>
          <div className="dl-summary-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span className="material-icons" style={{ color: "#dc2626", fontSize: 22 }}>warning</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Confirm Delete</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {deleteConfirm.type === "cc"
                    ? `Cost center ${deleteConfirm.key} and all its data will be permanently deleted.`
                    : `Account "${deleteConfirm.key}" and all associated budget/actuals rows will be permanently deleted.`}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
              Enter your admin password to proceed:
            </div>
            <input
              type="password"
              className="admin-text-input"
              style={{ width: "100%", marginBottom: 8 }}
              placeholder="Admin password"
              value={deletePw}
              onChange={e => { setDeletePw(e.target.value); setDeletePwErr(""); }}
              onKeyDown={e => { if (e.key === "Enter") confirmDeleteAction(); }}
              autoFocus
            />
            {deletePwErr && (
              <div className="admin-toast admin-toast--err" style={{ marginBottom: 8 }}>
                <span className="material-icons" style={{ fontSize: 13 }}>error</span>
                {deletePwErr}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="admin-btn admin-btn--ghost" onClick={() => setDeleteConfirm(null)} disabled={deletePwBusy}>
                Cancel
              </button>
              <button className="admin-btn admin-btn--danger" onClick={confirmDeleteAction} disabled={deletePwBusy || !deletePw}>
                {deletePwBusy ? "Verifying…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Business Units ──────────────────────────────────────────── */}
      {activeTab === "businessunits" && (
        <div className="admin-section">
          <AdminBusinessUnits />
        </div>
      )}

      {/* ── Tab: Users ───────────────────────────────────────────────────── */}
      {activeTab === "users" && (
        <div className="admin-section">
          <AdminUsers />
        </div>
      )}

    </div>
  );
};

export default AdminPanel;
